"use client"

import React, {
  useState, useRef, useEffect, useCallback, useMemo,
} from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism"
import { useTheme } from "next-themes"
import {
  X, ChevronRight, Globe, Loader2, Search, ChevronLeft, Copy, ChevronUp,
  AudioLines, FileUp, BookOpen, Square, ChevronDown, Bot, Sparkles,
  Shapes, ImageIcon, FileText, Terminal, Brain, Zap, Network, Check,
  CheckCircle2, XCircle, AlertTriangle, AtSign, Settings, StopCircle,
  CloudSync, ScanQrCode, Share2, Download, Upload, Eye, EyeOff, FolderOpen,
  Edit3, Globe2, Users, MessageSquare, Code2, Layers, Activity,
  MonitorPlay, Container, Database, ScanSearch, ShieldAlert, BarChart2,
  Cloud, LayoutTemplate, Gauge, Cpu, BarChart3, Hash, Slash,
  Send, Paperclip, Mic, MicOff,
  ThumbsUp, ThumbsDown, RefreshCw, LayoutGrid, Phone,
} from "lucide-react"
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts"

import { BUILTIN_CARDS, getCardContext, getSmartCardContext } from "../data/card-catalog"
import {
  PROVIDER_MODELS, MODEL_PRICING, resolveRoute,
  type ProviderModel, type ModelCategory,
} from "../data/provider-models"
import { getResearchApiUrl } from "../lib/auth"

// Re-export so existing importers (index.ts barrel, chat.askfinz.ai, desktop, …)
// keep resolving PROVIDER_MODELS / ProviderModel from this component unchanged.
// The single source of truth now lives in ../data/provider-models.
export { PROVIDER_MODELS }
export type { ProviderModel }
import { DiffReviewModal, type PendingDiff } from "./diff-review-modal" // AF-13 per-hunk review
import { VoiceCallDialog } from "./voice-call-dialog" // Call askFinz (phone queue)
import { AiLoginOverlay, useIsAuthedClient } from "./ai-login-gate" // login wall on public apps

// ─── Types ────────────────────────────────────────────────────────────────────

interface SourceDomain {
  domain: string
  url: string
  title?: string
}

interface AttachmentItem {
  type: "image" | "document"
  media_type: string
  data: string
  name: string
  dataUrl: string
  size: number
}

interface ToolCallRecord {
  id: string
  name: string
  input: Record<string, any>
  result?: string
  diff?: string
  running: boolean
  expanded: boolean
  awaitingPermission?: boolean
  denied?: boolean
  decisionId?: string  // backend permission_request decision_id (R129)
  danger?: boolean     // dangerous-tier → hide "Always" (R129)
}

interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  sources?: SourceDomain[]
  timestamp: Date
  isStreaming?: boolean
  thoughtTimeMs?: number
  thinkingContent?: string
  toolCalls?: ToolCallRecord[]
  attachments?: AttachmentItem[]
  model?: string
  inputTokens?: number
  outputTokens?: number
  cachedTokens?: number
}

interface SwarmCell {
  id: string
  messages: ChatMessage[]
  isRunning: boolean
  abortRef: React.MutableRefObject<AbortController | null>
}

interface FileItem {
  name: string
  path: string
  type: "file" | "folder"
}

export interface ChatDrawerProps {
  apiBaseUrl?: string
  appName?: string
  defaultOpen?: boolean
  position?: "right" | "left"
  width?: number
  projectId?: string
  fileItems?: FileItem[]
  isFullScreen?: boolean
  getAuthToken?: () => string | null
  sourceApp?: string
  memoryHref?: string
  /**
   * Per-call context the host app wants forwarded to the chat backend
   * with every message. Called fresh each turn so the function captures
   * the latest selection / phase / outline. Backends use this to
   * expand `@selection`, `@document`, `@notes`, etc. mentions and to
   * inject a Project Bible-style preamble.
   *
   * Wired by research.askfinz.ai writer; other hosts may ignore it.
   */
  metadata?: () => Record<string, unknown>
  /**
   * LOCAL inference seam (askFinz Desktop on-device vLLM, LF-C03/LF-V01).
   *
   * Optional + backward-compatible: web apps that don't pass these behave
   * EXACTLY as before (cloud-only). When provided:
   *   - `localModels` are merged into the model picker under a "Local" group.
   *   - `resolveLocalEndpoint(modelId)` is consulted FIRST in `runStream`; if it
   *     returns a non-null `{ url, headers }`, the turn is POSTed there in
   *     OpenAI chat-completions format and the standard OpenAI SSE
   *     (`choices[].delta.content`) is parsed — no auth_token / cloud body.
   *     Returning `null` (or omitting the prop) keeps the normal cloud path.
   */
  localModels?: Array<{ id: string; label: string; provider?: string }>
  resolveLocalEndpoint?: (modelId: string) => { url: string; headers?: Record<string, string> } | null
  /**
   * Empty-state suggestion chips. When provided, REPLACES the default discovery
   * prompts — the askFinz Desktop passes pane-specific actions (Browser / IDE /
   * CLI / Storage) so the drawer's quick-actions follow the active pane on top of
   * its regular chat. Backward-compatible: omit it for the default web prompts.
   */
  suggestions?: string[]
  /**
   * @-context seam (optional, host-fed). Given the raw outgoing message, return a
   * context block (e.g. from `@askfinz/shared-code` parseMentions/resolveMentions/
   * formatContextBlock against the host's adapters) to PREPEND to the message before
   * it's sent — so `@file`/`@git`/`@codebase`/`@problems` are resolved CLIENT-SIDE and
   * work on BOTH the cloud and local paths with no backend change. The host owns the
   * adapters (fs/git/search/diagnostics), so the same shared providers light up in the
   * webIDE, desktop and CLI. Return "" (or omit the prop) to keep behaviour unchanged.
   */
  resolveContextBlock?: (message: string) => Promise<string>
  /**
   * Unified-Orchestration P2 hint (optional, host-fed). Given the current draft
   * message, return a short label like "Better on Desktop IDE" to show as a chip
   * above the input — or null for no hint. The host computes it (e.g. via
   * `@askfinz/shared-code` `recommendSurface` gated to coding goals + its own
   * current surface), so the shared drawer stays decoupled from the routing vocab
   * and from each surface's package alias. Omit ⇒ no chip (unchanged).
   */
  getSurfaceHint?: (message: string) => string | null
  /**
   * P3: clicking the surface-hint chip hands the current task to the recommended
   * surface (the host POSTs to `/api/orchestrator/handover`). When provided, the
   * chip renders as a button; omit ⇒ the chip is a passive label.
   */
  onSurfaceHintClick?: (message: string) => void
  /**
   * P3 receive-side (optional, host-fed). The drawer polls `poll()` on a light
   * interval for handovers addressed to THIS surface and shows an "incoming
   * handover" prompt; Accept calls `accept(id)` (and the drawer drops the handed
   * message into the input so the user continues it — context already follows the
   * shared conversation key), Decline calls `decline(id)`. Omit ⇒ no polling.
   */
  handoverInbox?: {
    poll: () => Promise<Array<{ id: string; from_surface: string; message: string; reason: string }>>
    accept: (id: string) => Promise<void>
    decline: (id: string) => Promise<void>
    /**
     * Optional P4 SSE push endpoint (host-fed, returns the URL or null). When present,
     * the drawer subscribes via EventSource for *instant* handover delivery and only
     * falls back to `poll()` if the stream errors/closes. The handover endpoints need
     * auth (SECURITY V-105) and EventSource can't set headers: a same-origin host relies
     * on the auth cookie; a cross-origin host (desktop) resolves a URL carrying a
     * short-lived `st` token from POST /handover/stream-token, hence the Promise form.
     */
    streamUrl?: () => string | null | Promise<string | null>
  }
  /**
   * Optional host-fed list of @-context providers to surface in the `@` menu for
   * discoverability (the resolution is handled by `resolveContextBlock`; this just
   * makes them visible/insertable). Each surface passes the providers it supports
   * (e.g. webIDE: file/git/codebase/problems; CLI: file/git/codebase). `takesArg`
   * providers insert `@name:` (cursor parked for the arg); others insert `@name `.
   */
  contextProviders?: Array<{ name: string; description: string; takesArg?: boolean }>
}

// ─── Constants ────────────────────────────────────────────────────────────────
// NOTE: ProviderModel / PROVIDER_MODELS / ModelCategory / ModelPricing /
// MODEL_PRICING and the model-routing logic now live in
// ../data/provider-models (single source of truth, shared across surfaces).
// They are imported at the top of this file and PROVIDER_MODELS / ProviderModel
// are re-exported for backward compatibility.

// Backward-compat map for message model labels
const ALL_MODELS = PROVIDER_MODELS.map(m => ({
  id: m.id, label: m.name, vendor: m.provider, vendorLabel: m.providerLabel,
}))

const SLASH_COMMANDS = [
  // Session management
  { cmd: "clear", description: "Clear conversation history" },
  { cmd: "compact", description: "Summarize history to save context" },
  { cmd: "rewind", description: "Undo last N turn pairs — /rewind or /rewind 3" },
  { cmd: "export", description: "Export conversation as Markdown, JSON, or HTML" },
  { cmd: "background", description: "Background this session" },
  // Mode toggles
  { cmd: "plan", description: "Toggle plan mode (read-only, no writes/exec)" },
  { cmd: "think", description: "Toggle extended thinking (ultrathink for max)" },
  { cmd: "confirm", description: "Toggle confirmation mode (approve each write/exec)" },
  { cmd: "coordinator", description: "Toggle coordinator mode — agent orchestrates sub-agents" },
  { cmd: "auto", description: "Toggle auto mode (skip all confirmations)" },
  { cmd: "fast", description: "Toggle fast mode (speed-optimised inference)" },
  // Effort control
  { cmd: "effort", description: "Set effort level — /effort low|medium|high|max" },
  // Model & output
  { cmd: "model", description: "Set model — /model askfinz:pro" },
  { cmd: "bedrock", description: "Check AWS Bedrock status and available models" },
  { cmd: "output", description: "Set output style — /output markdown|plain|json" },
  // Memory
  { cmd: "remember", description: "Save a fact to persistent memory — /remember I prefer dark mode" },
  { cmd: "forget", description: "Remove a memory entry by key — /forget preference_key" },
  { cmd: "dream", description: "Distil last 7 days of session logs into project memories" },
  { cmd: "memory", description: "Show loaded ASKFINZ.md content" },
  { cmd: "memory-search", description: "Semantic memory search — /memory-search <query>" },
  { cmd: "team-memory", description: "Show shared team memory for this project" },
  { cmd: "subagents", description: "List recent sub-agent sessions spawned by this project" },
  // Git & code review
  { cmd: "commit-push-pr", description: "Create branch → commit → push → open PR" },
  { cmd: "security-review", description: "Security-focused code review of recent changes" },
  { cmd: "release-notes", description: "Generate release notes from git log" },
  { cmd: "pr-comments", description: "Fetch and address open PR comments" },
  { cmd: "review", description: "Automated code review of current changes" },
  { cmd: "diff-review", description: "Toggle Diff Review Mode — queue agent file writes for manual approval" },
  // Analytics & diagnostics
  { cmd: "cost", description: "Show session token usage and estimated cost" },
  { cmd: "status", description: "Show session status (messages, tokens, model)" },
  { cmd: "context", description: "Show context usage breakdown and suggestions" },
  { cmd: "insights", description: "Show session analytics — top tools, activity heatmap" },
  { cmd: "doctor", description: "Run health checks: API, MCP, memory, permissions" },
  { cmd: "usage", description: "Show 5h/7d token usage vs rate limits" },
  // Planning
  { cmd: "ultraplan", description: "Multi-agent planning — spawn parallel planning agents" },
  { cmd: "advisor", description: "Set secondary advisor model for real-time suggestions" },
  // Security & sandbox
  { cmd: "sandbox-toggle", description: "Toggle sandbox isolation (restrict file paths + network domains)" },
  { cmd: "bughunter", description: "Switch to bug-hunting mode (specialized system prompt)" },
  // Utilities
  { cmd: "tag", description: "Tag this session — /tag release-prep" },
  { cmd: "stickers", description: "Generate ASCII art sticker decoration" },
  { cmd: "thinkback", description: "Toggle extended thinking recording for this session" },
  { cmd: "thinkback-play", description: "Replay recorded thinking blocks at original speed" },
  { cmd: "tts", description: "Toggle text-to-speech for agent responses" },
  { cmd: "screenshot", description: "Toggle screenshot-to-code mode" },
  { cmd: "share", description: "Share this session — copies a read-only link to clipboard" },
  { cmd: "all-sessions", description: "List sessions across all your projects" },
  { cmd: "file-history", description: "Show diff history for a file — /file-history src/main.py" },
  { cmd: "coverage", description: "Visualize test coverage from coverage.xml or lcov.info" },
  { cmd: "security-scan", description: "Run dependency CVE scan (pip-audit + npm audit)" },
  { cmd: "quality", description: "Run code quality analysis (ruff + radon + mypy)" },
  { cmd: "monitor", description: "Check Prometheus alerts + targets + Grafana datasources" },
  // Swarm
  { cmd: "swarm", description: "Open swarm mode — run multiple agents in parallel" },
  // Card library
  { cmd: "library", description: "Browse the askFinz card library" },
  { cmd: "build", description: "Open the card builder to create a custom card" },
  // Public library RAG (LIBRARY_MONETIZATION_PLAN §13.3 / R6)
  { cmd: "askdocs", description: "Query the public library — /askdocs <question>. Charges 1+ cr per fresh chunk; cost shown before LLM call." },
  // Help
  { cmd: "help", description: "Show all slash commands" },
]

const TOOL_ICONS: Record<string, any> = {
  read_file: FileText, write_file: Edit3, edit_file: Edit3, exec_command: Terminal,
  list_files: FolderOpen, glob_files: FolderOpen, grep_files: Search, search_files: Search,
  get_file_info: FileText, web_search: Globe2, web_fetch: Globe2, spawn_agent: Bot,
  task_create: CheckCircle2, task_list: CheckCircle2, task_get: CheckCircle2, task_update: CheckCircle2,
  task_stop: XCircle, task_output: FileText, save_memory: BookOpen, list_memories: BookOpen,
  save_team_memory: Users, list_team_memories: Users, repl_start: Code2, repl_exec: Code2,
  lsp_completions: Layers, lsp_diagnostics: Zap, lsp_hover: AlertTriangle, lsp_definition: Sparkles,
  browser_automation: MonitorPlay, docker_compose_manager: Container, database_query: Database,
  code_quality_analyzer: ScanSearch, dependency_security_scan: ShieldAlert, test_coverage_visualizer: BarChart2,
  lighthouse_audit: Gauge, kubernetes_deploy: Cloud, terraform_plan: LayoutTemplate, monitoring_dashboard: Activity,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateId() {
  return Math.random().toString(36).slice(2, 9)
}

const parseMessageContent = (rawContent: string) => {
  let thought = null
  let response = rawContent
  const thinkStart = rawContent.indexOf("<think>")
  const thinkEnd = rawContent.indexOf("</think>")
  if (thinkStart !== -1) {
    if (thinkEnd !== -1) {
      thought = rawContent.substring(thinkStart + 7, thinkEnd).trim()
      response = (rawContent.substring(0, thinkStart) + rawContent.substring(thinkEnd + 8)).trim()
    } else {
      thought = rawContent.substring(thinkStart + 7).trim()
      response = rawContent.substring(0, thinkStart).trim()
    }
  }
  response = response.replace(/\[(\d+)\](?!\()/g, "[$1](#cite-$1)")
  if (thought && !response) {
    response = "*(The model concluded its internal reasoning but failed to output a distinct text response.)*"
  }
  return { thought, response }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const CHART_COLORS = ["#6366f1", "#22d3ee", "#f59e0b", "#10b981", "#f43f5e", "#a78bfa", "#34d399", "#fb923c"]

const ChartBlock = ({ raw }: { raw: string }) => {
  let cfg: any = null
  try { cfg = JSON.parse(raw) } catch { return <pre className="text-red-400 text-[11px] p-2">Invalid chart JSON</pre> }
  const { type = "line", title, data = [], xKey = "name", yKeys = [], colors = CHART_COLORS } = cfg
  const resolvedYKeys: { key: string; color: string }[] = yKeys.length > 0
    ? yKeys.map((y: any, i: number) => typeof y === "string" ? { key: y, color: colors[i] ?? CHART_COLORS[i % CHART_COLORS.length] } : { key: y.key, color: y.color ?? colors[i] ?? CHART_COLORS[i % CHART_COLORS.length] })
    : Object.keys(data[0] ?? {}).filter(k => k !== xKey).map((k, i) => ({ key: k, color: CHART_COLORS[i % CHART_COLORS.length] }))
  const commonProps = {
    data, margin: { top: 4, right: 16, left: -16, bottom: 0 },
    style: { fontSize: 11 },
  }
  const axisStyle = { fill: "rgb(148 163 184)", fontSize: 11 }
  const gridStyle = { stroke: "rgba(148,163,184,0.15)", strokeDasharray: "3 3" }
  return (
    <div className="my-3 rounded-xl border border-border-color bg-secondary/30 p-3">
      {title && <p className="text-[12px] font-semibold text-text-secondary mb-3 pl-1">{title}</p>}
      <ResponsiveContainer width="100%" height={200}>
        {type === "pie" ? (
          <PieChart>
            <Pie data={data} dataKey={resolvedYKeys[0]?.key ?? "value"} nameKey={xKey} cx="50%" cy="50%" outerRadius={72} label={({ name, percent }: any) => `${name} ${(((percent as number) ?? 0) * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
          </PieChart>
        ) : type === "area" ? (
          <AreaChart {...commonProps}>
            <CartesianGrid {...gridStyle} />
            <XAxis dataKey={xKey} tick={axisStyle} />
            <YAxis tick={axisStyle} />
            <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {resolvedYKeys.map(({ key, color }) => (
              <Area key={key} type="monotone" dataKey={key} stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} dot={false} />
            ))}
          </AreaChart>
        ) : type === "bar" ? (
          <BarChart {...commonProps}>
            <CartesianGrid {...gridStyle} />
            <XAxis dataKey={xKey} tick={axisStyle} />
            <YAxis tick={axisStyle} />
            <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {resolvedYKeys.map(({ key, color }) => <Bar key={key} dataKey={key} fill={color} radius={[3, 3, 0, 0]} />)}
          </BarChart>
        ) : (
          <LineChart {...commonProps}>
            <CartesianGrid {...gridStyle} />
            <XAxis dataKey={xKey} tick={axisStyle} />
            <YAxis tick={axisStyle} />
            <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {resolvedYKeys.map(({ key, color }) => (
              <Line key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  )
}

const CodeBlock = ({ node, inline, className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className || "")
  const lang = match ? match[1] : ""
  if (lang === "chart") return <ChartBlock raw={String(children).trim()} />
  const parentIsPre = node?.parent?.tagName === "pre" || node?.position?.start?.column === 1
  const isInline = !parentIsPre && (inline === true || (!match && !String(children).includes("\n")))
  return !isInline ? (
    <div className="relative my-2 group/code">
      <div className="flex items-center justify-between px-4 py-2 bg-secondary border-x border-t border-border-color rounded-t-2xl backdrop-blur-md">
        <span className="text-[10px] font-black uppercase tracking-widest text-text-secondary opacity-60">
          {lang || "TEXT"}
        </span>
        <button
          onClick={() => navigator.clipboard.writeText(String(children).replace(/\n$/, ""))}
          className="p-1.5 hover:bg-secondary rounded-md transition-colors text-text-secondary hover:text-text-primary"
        >
          <Copy size={12} />
        </button>
      </div>
      <div className="rounded-b-2xl border border-border-color overflow-hidden shadow-2xl">
        <SyntaxHighlighter
          {...props} style={vscDarkPlus} language={lang} PreTag="div"
          wrapLines wrapLongLines codeTagProps={{ style: { whiteSpace: "pre-wrap", wordBreak: "break-word" } }}
          customStyle={{ margin: 0, padding: "1.25rem", fontSize: "13px", lineHeight: "1.6", background: "rgba(10,10,12,0.8)", border: "none", whiteSpace: "pre-wrap", wordBreak: "break-word", overflowX: "hidden" }}
        >
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      </div>
    </div>
  ) : (
    <code className="px-1.5 py-0.5 bg-secondary rounded-md border border-border-color/30 text-accent font-bold text-[0.9em]" {...props}>
      {children}
    </code>
  )
}

function ToolCallBlock({ tc, onResolve }: { tc: ToolCallRecord; onResolve?: (decisionId: string, decision: "allow" | "deny", scope: "once" | "always") => void }) {
  const [expanded, setExpanded] = useState(tc.expanded)
  const Icon = TOOL_ICONS[tc.name] || Zap
  const statusColor = tc.denied ? "text-red-400" : tc.awaitingPermission ? "text-amber-400" : tc.running ? "text-blue-400" : tc.result !== undefined ? "text-green-400" : "text-muted-foreground"
  return (
    <div className={`my-1.5 rounded-lg border ${tc.denied ? "border-red-500/30 bg-red-500/5" : tc.awaitingPermission ? "border-amber-500/30 bg-amber-500/5" : "border-border-color/50 bg-secondary/30"}`}>
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors rounded-lg"
      >
        <Icon size={13} className={statusColor} />
        <span className="flex-1 text-[11px] font-mono font-semibold text-text-secondary truncate">{tc.name}</span>
        {tc.running && <Loader2 size={11} className="animate-spin text-blue-400 shrink-0" />}
        {tc.awaitingPermission && <span className="text-[10px] text-amber-400 shrink-0">awaiting approval</span>}
        {tc.denied && <span className="text-[10px] text-red-400 shrink-0">denied</span>}
        <ChevronRight size={11} className={`shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {/* R129: live approval prompt — Allow / Deny / (Always). Backend awaits this (else 60s→deny).
          "Always" persists a scoped allow rule (R111–R114); hidden for dangerous-tier. */}
      {tc.awaitingPermission && onResolve && (
        <div className="flex items-center gap-1.5 px-3 pb-2 pt-1">
          <span className="text-[10px] text-amber-400/80 flex-1">{tc.danger ? "⚠ destructive — review carefully" : "Allow this tool to run?"}</span>
          <button
            onClick={() => onResolve(tc.decisionId || tc.id, "allow", "once")}
            className="text-[10px] px-2 py-0.5 rounded bg-green-700/60 hover:bg-green-700 text-green-100 transition-colors"
          >Allow</button>
          {!tc.danger && (
            <button
              onClick={() => onResolve(tc.decisionId || tc.id, "allow", "always")}
              title="Allow and remember — auto-approve matching calls"
              className="text-[10px] px-2 py-0.5 rounded bg-green-900/50 hover:bg-green-800 text-green-300 transition-colors"
            >Always</button>
          )}
          <button
            onClick={() => onResolve(tc.decisionId || tc.id, "deny", "once")}
            className="text-[10px] px-2 py-0.5 rounded bg-red-800/60 hover:bg-red-800 text-red-100 transition-colors"
          >Deny</button>
        </div>
      )}
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {Object.keys(tc.input).length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Input</p>
              <pre className="text-[11px] text-text-secondary/80 font-mono whitespace-pre-wrap break-all bg-black/20 rounded p-2 max-h-40 overflow-y-auto">
                {JSON.stringify(tc.input, null, 2)}
              </pre>
            </div>
          )}
          {tc.result && (
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Result</p>
              <pre className="text-[11px] text-green-400/80 font-mono whitespace-pre-wrap break-all bg-black/20 rounded p-2 max-h-40 overflow-y-auto">
                {tc.result.slice(0, 2000)}{tc.result.length > 2000 ? "\n…(truncated)" : ""}
              </pre>
            </div>
          )}
          {tc.diff && (
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Diff</p>
              <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-black/20 rounded p-2 max-h-40 overflow-y-auto text-text-secondary/80">
                {tc.diff.slice(0, 1000)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AttachmentPreview({ att, onRemove }: { att: AttachmentItem; onRemove: () => void }) {
  return (
    <div className="relative group flex-shrink-0">
      {att.type === "image" ? (
        <img src={att.dataUrl} alt={att.name} className="h-16 w-16 object-cover rounded-lg border border-border-color" />
      ) : (
        <div className="h-16 w-16 flex flex-col items-center justify-center rounded-lg border border-border-color bg-secondary">
          <FileText size={20} className="text-text-secondary" />
          <span className="text-[8px] text-text-secondary mt-1 truncate w-full text-center px-1">{att.name.slice(0, 8)}</span>
        </div>
      )}
      <button
        onClick={onRemove}
        className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={9} className="text-white" />
      </button>
    </div>
  )
}

function SwarmCellView({
  cell, index, broadcast, onBroadcast, projectId, model, apiBaseUrl,
}: {
  cell: SwarmCell; index: number; broadcast: string; onBroadcast: (msg: string) => void
  projectId: string; model: string; apiBaseUrl: string
}) {
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }) }, [cell.messages])

  useEffect(() => {
    if (broadcast) {
      setInput(broadcast)
    }
  }, [broadcast])

  async function send(text?: string) {
    const msg = (text ?? input).trim()
    if (!msg || isLoading) return
    setInput("")
    setIsLoading(true)
    const ctrl = new AbortController()
    abortRef.current = ctrl
    try {
      const res = await fetch(`${apiBaseUrl}/api/v1/agent/${projectId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, model, swarm_cell_id: cell.id }),
        signal: ctrl.signal,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      if (reader) {
        let buf = ""
        const assistantId = generateId()
        cell.messages.push({ id: assistantId, role: "assistant", content: "", timestamp: new Date(), isStreaming: true })
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const events = buf.split("\n\n")
          buf = events.pop() ?? ""
          for (const ev of events) {
            const line = ev.split("\n").find((l) => l.startsWith("data: "))
            if (!line) continue
            try {
              const data = JSON.parse(line.slice(6))
              if (data.type === "text" && data.content) {
                const last = cell.messages[cell.messages.length - 1]
                if (last?.id === assistantId) last.content += data.content
              }
            } catch { /* skip */ }
          }
        }
        const last = cell.messages[cell.messages.length - 1]
        if (last?.id === assistantId) last.isStreaming = false
      }
    } catch { /* ignore */ }
    finally { setIsLoading(false) }
  }

  // Swarm cells now mirror the main chat tab visually: same bubble shape,
  // same text size (text-sm), same input affordance, same loader treatment.
  // Each cell gets a coloured edge so users can still tell agents apart.
  const accents = [
    { ring: "border-blue-500/40",    dot: "bg-blue-500"    },
    { ring: "border-purple-500/40",  dot: "bg-purple-500"  },
    { ring: "border-emerald-500/40", dot: "bg-emerald-500" },
    { ring: "border-orange-500/40",  dot: "bg-orange-500"  },
  ]
  const accent = accents[index % accents.length]
  return (
    <div className={`flex flex-col border rounded-xl overflow-hidden bg-bg-main ${accent.ring}`} style={{ minHeight: 0 }}>
      <div className="flex items-center justify-between px-3 py-2 bg-secondary/40 border-b border-border-color shrink-0">
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-text-primary">
          <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
          Agent {index + 1}
        </span>
        {isLoading && <Loader2 size={12} className="animate-spin text-text-secondary" />}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-sm">
        {cell.messages.length === 0 && (
          <div className="text-xs text-text-secondary italic px-1 py-2">
            No messages yet. Type below — broadcasts from the swarm bar appear here too.
          </div>
        )}
        {cell.messages.map((m) => {
          const isUser = m.role === "user"
          return (
            <div key={m.id} className={`flex flex-col ${isUser ? "items-end" : "items-start"} gap-1`}>
              <div className={`max-w-[88%] rounded-2xl px-3.5 py-2 leading-relaxed whitespace-pre-wrap break-words ${
                isUser
                  ? "bg-accent text-bg-main rounded-br-sm"
                  : "bg-secondary/80 text-text-primary rounded-bl-sm border border-border-color"
              }`}>
                {m.content || (m.isStreaming ? <Loader2 size={12} className="animate-spin" /> : "")}
              </div>
            </div>
          )
        })}
        <div ref={endRef} />
      </div>
      <div className="flex items-end gap-2 p-2 border-t border-border-color bg-bg-main shrink-0">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send() } }}
          placeholder="Message this agent…"
          rows={1}
          className="flex-1 resize-none text-sm bg-transparent border border-border-color rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/40 placeholder:text-text-secondary/60 max-h-24"
        />
        <button
          onClick={() => send()}
          disabled={!input.trim() || isLoading}
          className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-accent/15 hover:bg-accent/25 disabled:opacity-40 transition-colors shrink-0"
          aria-label="Send message"
        >
          <Send size={14} className="text-accent" />
        </button>
      </div>
    </div>
  )
}

// ─── Response complexity detection ───────────────────────────────────────────

function detectComplexResponse(text: string): boolean {
  const hasTable = /\|.+\|.+\|/.test(text)
  const codeBlocks = Math.floor((text.match(/```/g) ?? []).length / 2)
  const headings = (text.match(/^#{1,3}\s/gm) ?? []).length
  return hasTable || codeBlocks >= 2 || (headings >= 3 && text.length > 500)
}

// ─── Token cost pricing ───────────────────────────────────────────────────────

function calcTokenCost(modelId: string, outTok: number, inTok: number, cacTok: number) {
  const p = MODEL_PRICING[modelId]
  if (!p || p.free)    return { inputCost: 0, outputCost: 0, cachedCost: 0, totalCost: 0, isFree: true,  isFreeTier: false }
  if (p.freeTier)      return { inputCost: 0, outputCost: 0, cachedCost: 0, totalCost: 0, isFree: false, isFreeTier: true  }
  const inputCost  = (inTok  / 1_000_000) * p.inputPerMTok
  const outputCost = (outTok / 1_000_000) * p.outputPerMTok
  const cachedCost = p.cachedPerMTok ? (cacTok / 1_000_000) * p.cachedPerMTok : 0
  return { inputCost, outputCost, cachedCost, totalCost: inputCost + outputCost + cachedCost, isFree: false, isFreeTier: false }
}

function fmtCost(v: number) {
  if (v === 0) return "$0.00"
  if (v < 0.0001) return `$${v.toFixed(6)}`
  if (v < 0.001)  return `$${v.toFixed(5)}`
  if (v < 0.01)   return `$${v.toFixed(4)}`
  return `$${v.toFixed(3)}`
}

function fmtRate(r: number) { return `$${r}/MTok` }

// ─── Model usage tracking (daily local counter) ───────────────────────────────

function getModelUsageToday(modelId: string): number {
  try {
    const raw = localStorage.getItem(`af_mu_${modelId}`)
    if (!raw) return 0
    const { count, date } = JSON.parse(raw)
    return date === new Date().toDateString() ? (count ?? 0) : 0
  } catch { return 0 }
}

function trackModelUse(modelId: string) {
  try {
    const today = new Date().toDateString()
    const raw = localStorage.getItem(`af_mu_${modelId}`)
    let count = 0
    if (raw) { const p = JSON.parse(raw); if (p.date === today) count = p.count ?? 0 }
    localStorage.setItem(`af_mu_${modelId}`, JSON.stringify({ count: count + 1, date: today }))
  } catch {}
}

// ─── Auto-routing table ───────────────────────────────────────────────────────

type AutoTaskCategory =
  | "code"        // code generation, debugging, refactoring, agentic coding
  | "reasoning"   // math, logic, step-by-step analysis, research
  | "vision"      // image/screenshot attached, describe/analyse image
  | "longdoc"     // large paste, >8K tokens estimated, summarise doc
  | "multilingual"// non-English input or explicit translation request
  | "creative"    // story, poem, brainstorm, marketing copy
  | "fast"        // quick lookup, one-liner, simple Q&A
  | "default"

interface AutoCandidate {
  provider: string   // provider key sent to /api/chat/completions
  model: string      // exact API model ID
  label: string      // human-readable name shown in status bar
}

// Edit this table to change which models Auto dispatches to.
// Candidates are tried in order; first non-rate-limited wins.
const AUTO_ROUTING_TABLE: Record<AutoTaskCategory, AutoCandidate[]> = {
  code: [
    { provider: "nvidia_nim",      model: "qwen/qwen3-coder-480b-a35b-instruct", label: "Qwen 3 Coder 480B" },
    { provider: "nvidia_nim",      model: "deepseek-ai/deepseek-v4-pro",         label: "DeepSeek V4 Pro" },
    { provider: "google_ai_studio",model: "gemini-2.5-pro",                      label: "Gemini 2.5 Pro" },
    { provider: "openrouter",      model: "anthropic/claude-sonnet-4-6",         label: "Claude Sonnet 4.6" },
  ],
  reasoning: [
    { provider: "nvidia_nim",      model: "moonshotai/kimi-k2-instruct",         label: "Kimi K2" },
    { provider: "openrouter",      model: "deepseek/deepseek-r1",                label: "DeepSeek R1" },
    { provider: "groq",            model: "qwen/qwen3-32b",                      label: "Qwen 3 32B (Groq)" },
    { provider: "openrouter",      model: "anthropic/claude-sonnet-4-6",         label: "Claude Sonnet 4.6" },
  ],
  vision: [
    { provider: "google_ai_studio",model: "gemini-2.5-pro",                      label: "Gemini 2.5 Pro" },
    { provider: "google_ai_studio",model: "gemini-3-flash-preview",              label: "Gemini 3 Flash" },
    { provider: "nvidia_nim",      model: "meta/llama-4-maverick-17b-128e-instruct", label: "Llama 4 Maverick" },
    { provider: "openrouter",      model: "anthropic/claude-sonnet-4-6",         label: "Claude Sonnet 4.6" },
  ],
  longdoc: [
    { provider: "google_ai_studio",model: "gemini-2.5-pro",                      label: "Gemini 2.5 Pro (1M ctx)" },
    { provider: "nvidia_nim",      model: "moonshotai/kimi-k2-instruct",         label: "Kimi K2 (256K ctx)" },
    { provider: "cohere",          model: "command-a-03-2025",                   label: "Command A (256K ctx)" },
    { provider: "openrouter",      model: "anthropic/claude-sonnet-4-6",         label: "Claude Sonnet 4.6" },
  ],
  multilingual: [
    { provider: "cohere",          model: "command-a-03-2025",                   label: "Command A" },
    { provider: "openrouter",      model: "qwen/qwen3-max",                      label: "Qwen 3 Max" },
    { provider: "mistral",         model: "mistral-large-latest",                label: "Mistral Large 3" },
    { provider: "google_ai_studio",model: "gemini-2.5-flash",                   label: "Gemini 2.5 Flash" },
  ],
  creative: [
    { provider: "mistral",         model: "mistral-large-latest",                label: "Mistral Large 3" },
    { provider: "openrouter",      model: "x-ai/grok-3-beta",                   label: "Grok 3" },
    { provider: "openrouter",      model: "anthropic/claude-sonnet-4-6",         label: "Claude Sonnet 4.6" },
    { provider: "google_ai_studio",model: "gemini-2.5-flash",                   label: "Gemini 2.5 Flash" },
  ],
  fast: [
    { provider: "google_ai_studio",model: "gemini-2.5-flash",                   label: "Gemini 2.5 Flash" },
    { provider: "groq",            model: "meta-llama/llama-4-scout-17b-16e-instruct", label: "Llama 4 Scout (Groq)" },
    { provider: "cerebras",        model: "qwen-3-235b-a22b-instruct-2507",     label: "Qwen 3 235B (Cerebras)" },
    { provider: "openrouter",      model: "anthropic/claude-haiku-4-5",          label: "Claude Haiku 4.5" },
  ],
  default: [
    { provider: "google_ai_studio",model: "gemini-2.5-flash",                   label: "Gemini 2.5 Flash" },
    { provider: "openrouter",      model: "anthropic/claude-sonnet-4-6",         label: "Claude Sonnet 4.6" },
    { provider: "groq",            model: "llama-3.3-70b-versatile",            label: "Llama 3.3 70B (Groq)" },
    { provider: "google_ai_studio",model: "gemini-2.5-pro",                     label: "Gemini 2.5 Pro" },
  ],
}

// Keyword sets for each category (order matters — more specific first)
const _CODE_RX = /\b(code|function|class|bug|debug|refactor|implement|syntax|compile|error|exception|script|typescript|javascript|python|rust|golang|sql|api|endpoint|hook|component|algorithm|leetcode|array|loop|regex|import|export|async|await|promise|dockerfile|kubernetes|ci.?cd)\b/i
const _REASON_RX = /\b(solve|prove|proof|derive|calculate|compute|reason|math|equation|formula|logic|step.by.step|explain why|analyse|analyze|compare|evaluate|research|hypothesis|theorem|probability|statistics|optimization|prime|integral|derivative|matrix|eigenvalue)\b/i
const _CREATIVE_RX = /\b(write a (story|poem|essay|blog|email|ad|caption|script|song|letter)|brainstorm|creative|marketing|slogan|metaphor|narrative|fiction|describe in a|imagine)\b/i
const _MULTILINGUAL_RX = /\b(translate|in (spanish|french|german|arabic|chinese|japanese|korean|hindi|portuguese|italian|russian|dutch|turkish)|en français|auf deutsch|en español|中文|日本語|한국어|العربية)\b/i

function classifyPrompt(text: string, hasAttachments: boolean, estimatedTokens: number): AutoTaskCategory {
  if (hasAttachments) return "vision"
  if (estimatedTokens > 8000) return "longdoc"
  if (_CODE_RX.test(text)) return "code"
  if (_REASON_RX.test(text)) return "reasoning"
  if (_MULTILINGUAL_RX.test(text)) return "multilingual"
  if (_CREATIVE_RX.test(text)) return "creative"
  if (text.trim().length <= 120) return "fast"   // very short prompt → fast model
  return "default"
}

// ─── ModelPickerPanel ─────────────────────────────────────────────────────────

const CATEGORY_FILTERS: { id: ModelCategory; label: string }[] = [
  { id: "all", label: "All" },
  { id: "free", label: "🆓 Free" },
  { id: "fast", label: "Fast" },
  { id: "reasoning", label: "Reasoning" },
  { id: "coding", label: "Coding" },
  { id: "vision", label: "Vision" },
]

const PROVIDER_ORDER = ["local", "askfinz", "anthropic", "google", "nvidia", "mistral", "cerebras", "groq", "cohere", "github", "bedrock", "openrouter"]

function ModelPickerPanel({
  selectedModel,
  onSelect,
  onClose,
  freeModels = [],
  freeModelsLoading = false,
  localModels = [],
}: {
  selectedModel: string
  onSelect: (id: string) => void
  onClose: () => void
  freeModels?: ProviderModel[]
  freeModelsLoading?: boolean
  localModels?: ProviderModel[]
}) {
  const [search, setSearch] = useState("")
  const [activeFilter, setActiveFilter] = useState<ModelCategory>("all")
  const [usage, setUsage] = useState<Record<string, number>>({})

  const allModels = useMemo(() => {
    // Local (on-device) models first, then cloud catalog, then free OpenRouter.
    const seen = new Set([...localModels, ...PROVIDER_MODELS].map(m => m.id))
    return [...localModels, ...PROVIDER_MODELS, ...freeModels.filter(m => !seen.has(m.id))]
  }, [freeModels, localModels])

  useEffect(() => {
    const u: Record<string, number> = {}
    for (const m of allModels) u[m.id] = getModelUsageToday(m.id)
    setUsage(u)
  }, [allModels])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return allModels.filter(m => {
      const matchSearch = !q ||
        m.name.toLowerCase().includes(q) ||
        m.provider.toLowerCase().includes(q) ||
        m.providerLabel.toLowerCase().includes(q) ||
        m.tags.some(t => t.toLowerCase().includes(q)) ||
        m.description.toLowerCase().includes(q)
      const matchCat = activeFilter === "all" || m.categories.includes(activeFilter)
      return matchSearch && matchCat
    })
  }, [search, activeFilter, allModels])

  // Group by provider in display order; unknown providers appended at end
  const grouped = useMemo(() => {
    const map: Record<string, ProviderModel[]> = {}
    for (const m of filtered) {
      if (!map[m.provider]) map[m.provider] = []
      map[m.provider].push(m)
    }
    const known = PROVIDER_ORDER.filter(p => map[p]?.length).map(p => ({
      provider: p, label: map[p][0].providerLabel, models: map[p],
    }))
    const unknown = Object.keys(map).filter(p => !PROVIDER_ORDER.includes(p)).map(p => ({
      provider: p, label: map[p][0].providerLabel, models: map[p],
    }))
    return [...known, ...unknown]
  }, [filtered])

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-bg-main">
      {/* Header */}
      <div className="shrink-0 px-3 pt-3 pb-2 border-b border-border-color space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search models, providers, or tags…"
              className="w-full pl-8 pr-3 py-1.5 text-[11px] bg-secondary border border-border-color rounded-lg outline-none focus:border-accent/50 text-text-primary placeholder:text-text-secondary"
            />
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex gap-1 flex-wrap">
          {CATEGORY_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide transition-colors ${
                activeFilter === f.id
                  ? "bg-accent text-bg-main"
                  : "bg-secondary text-text-secondary hover:text-text-primary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Model list */}
      <div className="flex-1 overflow-y-auto askfinz-invisible-scrollbar">
        {/* Auto mode card — always first when no search and filter=all */}
        {!search && activeFilter === "all" && (
          <div className="px-2 pt-2">
            <button
              onClick={() => { onSelect("auto"); onClose() }}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left mb-1 ${
                selectedModel === "auto"
                  ? "border-accent bg-accent/10"
                  : "border-border-color hover:border-accent/40 hover:bg-state-hover"
              }`}
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent via-purple-500 to-blue-500 flex items-center justify-center shrink-0 shadow-lg">
                <Sparkles size={15} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[12px] font-bold text-text-primary">Auto</span>
                  <span className="px-1.5 py-0.5 rounded-full bg-accent/20 text-accent text-[8px] font-bold uppercase tracking-wide">Recommended</span>
                </div>
                <p className="text-[10px] text-text-secondary leading-relaxed">
                  askFinz selects the best model per message — balancing speed, reasoning depth, and cost automatically.
                </p>
              </div>
              {selectedModel === "auto" && <Check size={14} className="text-accent shrink-0" />}
            </button>
          </div>
        )}

        {/* Provider groups */}
        {grouped.map(group => (
          <div key={group.provider} className="px-2 py-1">
            <div className="flex items-center gap-2 px-1 mb-1.5 mt-1">
              <span className="text-[8px] font-black uppercase tracking-[0.15em] text-text-secondary/50">{group.label}</span>
              <div className="flex-1 h-px bg-border-color/30" />
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {group.models.map(model => {
                const used = usage[model.id] ?? 0
                const limitVal = model.rpd ?? (model.rpm ? model.rpm * 60 * 24 : 0)
                const pct = limitVal > 0 ? Math.min(100, (used / limitVal) * 100) : 0
                const isSelected = selectedModel === model.id

                return (
                  <button
                    key={model.id}
                    onClick={() => { onSelect(model.id); onClose() }}
                    className={`flex flex-col p-2.5 rounded-xl border transition-all text-left group relative overflow-hidden ${
                      isSelected
                        ? "border-accent bg-accent/10"
                        : "border-border-color hover:border-accent/30 hover:bg-state-hover"
                    }`}
                  >
                    {/* Provider badge + model name */}
                    <div className="flex items-start gap-2 mb-2">
                      {model.logoPath ? (
                        <img
                          src={model.logoPath}
                          alt={model.providerLabel}
                          className="w-6 h-6 rounded-md object-contain shrink-0 bg-secondary p-0.5"
                          onError={e => { (e.target as HTMLImageElement).style.display = "none" }}
                        />
                      ) : (
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 text-[8px] font-black ${model.providerColor}`}>
                          {model.providerInitials}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-text-primary leading-tight truncate">{model.name}</p>
                        <p className="text-[8px] text-text-secondary/70 truncate">{model.contextK}K ctx</p>
                      </div>
                      {isSelected && <Check size={10} className="text-accent shrink-0 mt-0.5" />}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-0.5 mb-2">
                      {model.tags.includes("free") && (
                        <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[7px] font-bold leading-none uppercase tracking-wide">FREE</span>
                      )}
                      {model.tags.filter(t => t !== "free").slice(0, 2).map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 rounded-full bg-secondary/80 text-text-secondary text-[7px] font-semibold leading-none">{tag}</span>
                      ))}
                    </div>

                    {/* Usage progress bar */}
                    {(model.rpd || model.rpm) && (
                      <div className="mb-2">
                        <div className="flex justify-between items-center mb-0.5">
                          <span className="text-[7px] text-text-secondary/50 font-medium">
                            {model.rpd ? `${used}/${model.rpd} req/day` : `${model.rpm} rpm`}
                          </span>
                          <span className="text-[7px] text-text-secondary/40">resets {model.resetLabel}</span>
                        </div>
                        <div className="h-0.5 bg-secondary rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              pct > 85 ? "bg-red-500" : pct > 60 ? "bg-amber-500" : "bg-accent"
                            }`}
                            style={{ width: `${Math.max(2, pct)}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-[9px] text-text-secondary leading-relaxed line-clamp-3">{model.description}</p>

                    {/* Use cases */}
                    <div className="mt-1.5 flex flex-wrap gap-0.5">
                      {model.useCases.slice(0, 2).map(uc => (
                        <span key={uc} className="text-[7px] text-accent/60 font-medium">{uc}</span>
                      ))}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ))}

        {grouped.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-text-secondary">
            {freeModelsLoading && activeFilter === "free" ? (
              <>
                <Loader2 size={20} className="mb-2 opacity-50 animate-spin" />
                <p className="text-xs opacity-60">Loading free models…</p>
              </>
            ) : (
              <>
                <Search size={20} className="mb-2 opacity-30" />
                <p className="text-xs opacity-60">No models match your search</p>
              </>
            )}
          </div>
        )}

        <div className="h-3" />
      </div>
    </div>
  )
}

// ─── Storage keys ─────────────────────────────────────────────────────────────

const STORAGE_KEY = "askfinz_chat_drawer_open"
const HISTORY_KEY = "askfinz_chat_history"
const MODEL_KEY = "askfinz_chat_model"
const MAX_HISTORY = 40

// ─── Main Component ───────────────────────────────────────────────────────────

export function ChatDrawer({
  apiBaseUrl = "",
  appName = "askFinz",
  defaultOpen = false,
  position = "right",
  width = 380,
  projectId,
  fileItems: externalFileItems,
  isFullScreen = false,
  getAuthToken,
  sourceApp = "chat",
  memoryHref = "/settings/memory",
  localModels,
  resolveLocalEndpoint,
  metadata,
  suggestions: suggestionsProp,
  resolveContextBlock,
  getSurfaceHint,
  onSurfaceHintClick,
  handoverInbox,
  contextProviders,
}: ChatDrawerProps) {
  const { resolvedTheme } = useTheme()
  const isDarkTheme = resolvedTheme === "dark" || !resolvedTheme

  // Login wall: on apps browsable without auth (shop/stream/news) the drawer
  // still renders, but an anonymous visitor sees a blurred panel + login card
  // instead of a usable composer. Real enforcement is server-side (/api/chat
  // 401s anonymous); this is the matching UI. Optimistic (assumes authed until
  // the effect resolves) so logged-in users never see a flash.
  const aiAuthed = useIsAuthedClient()

  // ── Core UI state ──────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen)
  const [activeTab, setActiveTab] = useState<"chat" | "swarm">("chat")
  // Dynamic projectId injected by IDE page via 'set-chat-drawer-project' custom event
  const [dynamicProjectId, setDynamicProjectId] = useState<string | undefined>(projectId)
  const effectiveProjectId = dynamicProjectId ?? projectId

  // ── Messages ───────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([])

  // ── Input ──────────────────────────────────────────────────────────────────
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ── P3/P4 incoming handovers (host-fed inbox; SSE push, poll fallback) ───────
  const [incomingHandovers, setIncomingHandovers] = useState<Array<{ id: string; from_surface: string; message: string; reason: string }>>([])
  useEffect(() => {
    if (!handoverInbox) return
    let alive = true
    let es: EventSource | null = null
    let pollIv: ReturnType<typeof setInterval> | null = null
    type H = { id: string; from_surface: string; message: string; reason: string }
    const norm = (r: { id: string; from_surface?: string; message?: string; reason?: string }): H =>
      ({ id: r.id, from_surface: r.from_surface ?? "", message: r.message ?? "", reason: r.reason ?? "" })

    const poll = async () => {
      try {
        const items = await handoverInbox.poll()
        if (alive) setIncomingHandovers(Array.isArray(items) ? items : [])
      } catch { /* best-effort */ }
    }
    const startPolling = () => {
      if (pollIv || !alive) return
      void poll()
      pollIv = setInterval(poll, 12000)
    }
    const stopPolling = () => { if (pollIv) { clearInterval(pollIv); pollIv = null } }

    // Prefer the P4 SSE push when the host supplies a stream URL.
    const openStream = (url: string | null) => {
      if (!alive) return
      if (url && typeof EventSource !== "undefined") {
        try {
          es = new EventSource(url)
          es.addEventListener("init", (e) => {
            try { const arr = JSON.parse((e as MessageEvent).data); if (alive) setIncomingHandovers(Array.isArray(arr) ? arr.map(norm) : []) } catch { /* */ }
          })
          es.addEventListener("handover", (e) => {
            try {
              const r = norm(JSON.parse((e as MessageEvent).data))
              if (alive) setIncomingHandovers((prev) => prev.some((x) => x.id === r.id) ? prev : [...prev, r])
            } catch { /* */ }
          })
          es.onopen = () => stopPolling()
          es.onerror = () => { startPolling() } // stream dropped → degrade to polling (EventSource auto-reconnects too)
        } catch { startPolling() }
      } else {
        startPolling()
      }
    }
    Promise.resolve()
      .then(() => handoverInbox.streamUrl?.() ?? null)
      .then(openStream, () => startPolling())

    return () => { alive = false; stopPolling(); if (es) es.close() }
  }, [handoverInbox])
  const acceptHandover = useCallback(async (h: { id: string; message: string }) => {
    if (!handoverInbox) return
    try {
      await handoverInbox.accept(h.id)
      setIncomingHandovers((prev) => prev.filter((x) => x.id !== h.id))
      if (h.message) { setInput(h.message); textareaRef.current?.focus() }
    } catch { /* leave it in the list to retry */ }
  }, [handoverInbox])
  const declineHandover = useCallback(async (id: string) => {
    if (!handoverInbox) return
    try { await handoverInbox.decline(id) } catch { /* */ }
    setIncomingHandovers((prev) => prev.filter((x) => x.id !== id))
  }, [handoverInbox])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const conversationIdRef = useRef<string>(Math.random().toString(36).slice(2, 18))

  // ── Message editing ────────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState("")

  // ── Thinking phase cycling + dislike modal + tabs ─────────────────────────
  const THINKING_PHASES_DRAWER = ["Thinking…", "Reviewing…", "Writing…", "Reading…", "Searching…", "Synthesizing…"]
  const [thinkingPhaseIdx, setThinkingPhaseIdx] = useState(0)
  const [drawerDislikeModal, setDrawerDislikeModal] = useState<{ open: boolean; msgId: string }>({ open: false, msgId: "" })
  const [drawerDislikeFeedback, setDrawerDislikeFeedback] = useState("")
  const [drawerMsgTabs, setDrawerMsgTabs] = useState<Record<string, string>>({})
  const [drawerCardPickerOpen, setDrawerCardPickerOpen] = useState(false)
  const [drawerCardSearch, setDrawerCardSearch] = useState("")

  function extractDrawerSources(content: string) {
    const refs = new Set<number>()
    const refRx = /\[(\d+)\]/g; let rm: RegExpExecArray | null
    while ((rm = refRx.exec(content)) !== null) refs.add(parseInt(rm[1]))
    const links: string[] = []
    const linkRx = /\]\(([^)]+)\)/g; let lm: RegExpExecArray | null
    while ((lm = linkRx.exec(content)) !== null) links.push(lm[1])
    return Array.from(refs).map((num, i) => {
      const url = links[i - 1] ?? undefined
      let domain = `source${num}`
      if (url) {
        try {
          domain = new URL(url).hostname.replace(/^www\./, "")
        } catch {
          // AI may produce non-URL refs like `[1] Vaswani et al. (2017). ...`
          // — the link extractor would catch any `](xxx)` pattern. Fall back
          // gracefully instead of crashing the whole drawer render.
          domain = `source${num}`
        }
      }
      return { num, domain, url }
    })
  }

  useEffect(() => {
    if (!isLoading) { setThinkingPhaseIdx(0); return }
    const t = setInterval(() => setThinkingPhaseIdx(p => (p + 1) % THINKING_PHASES_DRAWER.length), 2200)
    return () => clearInterval(t)
  }, [isLoading])

  // ── Model selection ────────────────────────────────────────────────────────
  const [selectedModel, setSelectedModel] = useState("gemini-2.5-flash")
  const [showCallDialog, setShowCallDialog] = useState(false) // Call askFinz
  const [showModelPicker, setShowModelPicker] = useState(false)
  const [showModelPanel, setShowModelPanel] = useState(false)
  const modelPickerRef = useRef<HTMLDivElement>(null)
  const [freeModels, setFreeModels] = useState<ProviderModel[]>([])
  const [freeModelsLoading, setFreeModelsLoading] = useState(false)
  // Anthropic (Claude) models: the LIVE list from GET /v1/models, served by
  // chat.askfinz.ai/api/chat/models?provider=anthropic (orchestrator-backed, 24 h
  // cache). Bare Claude ids, routed as provider "anthropic".
  const [anthropicModels, setAnthropicModels] = useState<ProviderModel[]>([])
  // LOCAL inference seam: lift the host's `localModels` into the picker's
  // ProviderModel shape so they render in a "Local" group. Empty when the host
  // passes nothing (web apps) — fully backward-compatible.
  const localProviderModels = useMemo<ProviderModel[]>(() => {
    if (!localModels?.length) return []
    return localModels.map(m => ({
      id: m.id,
      provider: m.provider ?? "local",
      providerLabel: "Local",
      providerInitials: "LO",
      providerColor: "bg-emerald-500/20 text-emerald-400",
      name: m.label,
      tags: ["local", "on-device", "private"],
      description: "Runs entirely on this device — no cloud round-trip, no data leaves the machine.",
      useCases: ["Offline use", "Private inference", "Low-latency local chat"],
      resetLabel: "on-device", contextK: 32,
      categories: ["all"] as ModelCategory[],
    }))
  }, [localModels])
  const [autoRouteLabel, setAutoRouteLabel] = useState<string | null>(null)
  // Gap 5: orchestrator phase display during streaming
  const [orchPhase, setOrchPhase] = useState<string>("")

  useEffect(() => {
    setFreeModelsLoading(true)
    fetch(`${apiBaseUrl}/api/chat/models?provider=openrouter&free=true`)
      .then(r => r.json())
      .then(data => {
        const raw: any[] = data?.providers?.openrouter?.models ?? []
        const mapped: ProviderModel[] = raw.map((m: any) => ({
          id: m.id,
          provider: "openrouter",
          providerLabel: "OpenRouter (Free)",
          providerInitials: "OR",
          providerColor: "bg-green-500/20 text-green-400",
          name: m.name,
          tags: ["free"],
          description: m.description || "",
          useCases: [],
          resetLabel: "daily",
          contextK: Math.round((m.contextLength || 8000) / 1000),
          categories: ["all", "free"] as ModelCategory[],
        }))
        setFreeModels(mapped)
      })
      .catch(() => {})
      .finally(() => setFreeModelsLoading(false))
  }, [])

  useEffect(() => {
    fetch(`${apiBaseUrl}/api/chat/models?provider=anthropic`)
      .then(r => r.json())
      .then(data => {
        const raw: any[] = data?.providers?.anthropic?.models ?? []
        setAnthropicModels(raw.map((m: any) => ({
          id: m.id,
          provider: "anthropic",
          providerLabel: "Anthropic",
          providerInitials: "AN",
          providerColor: "bg-orange-500/20 text-orange-400",
          name: m.name,
          tags: m.tags ?? [],
          description: m.description || "",
          useCases: [],
          resetLabel: "monthly credit",
          contextK: Math.round((m.contextLength || 200000) / 1000),
          categories: ["all", "reasoning", "coding", "vision"] as ModelCategory[],
        })))
      })
      .catch(() => {})
  }, [])

  // ── Agent mode features ────────────────────────────────────────────────────
  const [thinkingMode, setThinkingMode] = useState<"off" | "on" | "adaptive">("adaptive")
  const [coordinatorMode, setCoordinatorMode] = useState(false)
  const [planMode, setPlanMode] = useState(false)
  const [confirmMode, setConfirmMode] = useState(false)  // R133: /confirm — gate each write/exec for approval
  // AF-13: per-hunk diff review. When on (toggle via /review), agent write_file/edit_file results are
  // captured into pendingDiffs and surfaced in the DiffReviewModal for per-hunk keep/discard.
  const [diffReviewMode, setDiffReviewMode] = useState(false)
  const [pendingDiffs, setPendingDiffs] = useState<PendingDiff[]>([])
  const diffReviewModeRef = useRef(diffReviewMode)
  useEffect(() => { diffReviewModeRef.current = diffReviewMode }, [diffReviewMode])
  const preWriteSnapshotsRef = useRef<Map<string, { path: string; oldContent: string }>>(new Map())
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [ttsEnabled, setTtsEnabled] = useState(false)
  const [screenshotMode, setScreenshotMode] = useState(false)

  // ── Attachments ────────────────────────────────────────────────────────────
  const [attachments, setAttachments] = useState<AttachmentItem[]>([])

  // ── Slash command popup ────────────────────────────────────────────────────
  const [slashPopup, setSlashPopup] = useState<{ open: boolean; filter: string; idx: number }>({
    open: false, filter: "", idx: 0,
  })

  // ── @ mention popup ────────────────────────────────────────────────────────
  const [mentionPopup, setMentionPopup] = useState<{ open: boolean; filter: string; idx: number; start: number }>({
    open: false, filter: "", idx: 0, start: -1,
  })
  const [availableFiles, setAvailableFiles] = useState<FileItem[]>(externalFileItems ?? [])
  const [mentionedPaths, setMentionedPaths] = useState<string[]>([])

  // ── Swarm ──────────────────────────────────────────────────────────────────
  const [swarmCells, setSwarmCells] = useState<SwarmCell[]>([1, 2].map(() => ({
    id: generateId(), messages: [], isRunning: false, abortRef: { current: null },
  })))
  const [swarmBroadcast, setSwarmBroadcast] = useState("")
  const [swarmInput, setSwarmInput] = useState("")

  // ── Restore persisted state ────────────────────────────────────────────────
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored !== null) setIsOpen(stored === "true")
    } catch {}
    try {
      const raw = localStorage.getItem(HISTORY_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[]
        setMessages(parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) })))
      }
    } catch {}
    try {
      const savedModel = localStorage.getItem(MODEL_KEY)
      if (savedModel) setSelectedModel(savedModel)
    } catch {}
  }, [])

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(isOpen)) } catch {}
  }, [isOpen])

  useEffect(() => {
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-MAX_HISTORY))) } catch {}
  }, [messages])

  useEffect(() => {
    try { localStorage.setItem(MODEL_KEY, selectedModel) } catch {}
  }, [selectedModel])

  // ── CSS variable for body shift ────────────────────────────────────────────
  // On mobile (< 768px) the drawer goes full-width so there's no room to shift
  // the body — that would produce a blank margin stripe on a 390px screen.
  const isRight = position === "right"
  const effectiveWidth = typeof window !== "undefined" && window.innerWidth < 768
    ? window.innerWidth
    : width

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty("--chat-drawer-width", `${isOpen ? effectiveWidth : 0}px`)
    }
  }, [isOpen, effectiveWidth])

  // ── Body margin shift ──────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof document === "undefined") return
    // On mobile the drawer overlays full-width — don't shift the body or the
    // page scrolls off-screen sideways.
    if (window.innerWidth < 768) return
    const body = document.body
    if (isOpen) {
      body.style[isRight ? "marginRight" : "marginLeft"] = `${effectiveWidth}px`
      body.style.transition = "margin 0.35s cubic-bezier(0.4,0,0.2,1)"
    } else {
      body.style.marginRight = "0"
      body.style.marginLeft = "0"
    }
    return () => { body.style.marginRight = "0"; body.style.marginLeft = "0" }
  }, [isOpen, isRight, effectiveWidth])

  // ── Toggle event from TopNav / buddy sprite ────────────────────────────────
  useEffect(() => {
    const toggleHandler = () => setIsOpen((o) => !o)
    window.addEventListener("toggle-chat-drawer", toggleHandler)

    // Allow any page to inject a projectId via custom event
    const projectHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId: string }>).detail
      if (detail?.projectId && !projectId) {
        setDynamicProjectId(detail.projectId)
      }
    }
    window.addEventListener("set-chat-drawer-project", projectHandler)

    // Clear conversation when "New Session" is clicked or /new page mounts
    const clearHandler = () => {
      abortRef.current?.abort()
      setMessages([])
      setInput("")
      setSessionId(null)
      setEditingId(null)
      setAttachments([])
      conversationIdRef.current = Math.random().toString(36).slice(2, 18)
    }
    window.addEventListener("askfinz-clear-chat", clearHandler)

    // Open the drawer + pre-fill the input. Used by external surfaces
    // (e.g. the research writer's selection toolbar's "askFinz" button,
    // and the map app's "Analyse area" button which forwards a base64
    // image of the current map view for the model to vision-analyse).
    const openWithHandler = (e: Event) => {
      const detail = (e as CustomEvent<{
        prompt?: string;
        focus?: boolean;
        attachments?: Array<{ type: "image" | "document"; media_type: string; data: string; name: string }>;
      }>).detail || {}
      setIsOpen(true)
      if (typeof detail.prompt === "string") setInput(detail.prompt)
      if (Array.isArray(detail.attachments) && detail.attachments.length) {
        // Convert the base64 payload into the AttachmentItem shape the
        // drawer's send pipeline expects (which carries both a dataUrl
        // for preview and the raw base64 for the API call).
        const items: AttachmentItem[] = detail.attachments.map(a => ({
          type: a.type,
          media_type: a.media_type,
          data: a.data,
          name: a.name || (a.type === "image" ? "image.png" : "file.bin"),
          dataUrl: `data:${a.media_type};base64,${a.data}`,
          size: Math.ceil(a.data.length * 0.75),
        }))
        setAttachments(items)
      }
      if (detail.focus !== false) {
        setTimeout(() => {
          const el = document.querySelector(".chat-drawer textarea, .chat-drawer input[type=\"text\"]") as HTMLElement | null
          el?.focus?.()
        }, 60)
      }
    }
    window.addEventListener("chat-drawer:open-with", openWithHandler)

    return () => {
      window.removeEventListener("toggle-chat-drawer", toggleHandler)
      window.removeEventListener("set-chat-drawer-project", projectHandler)
      window.removeEventListener("askfinz-clear-chat", clearHandler)
      window.removeEventListener("chat-drawer:open-with", openWithHandler)
    }
  }, [projectId])

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return
    const isStreaming = messages.some((m) => m.isStreaming)
    if (isStreaming) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" })
    } else {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50)
    }
  }, [messages, isOpen])

  // ── Model picker close on outside click ───────────────────────────────────
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node)) {
        setShowModelPicker(false)
      }
    }
    if (showModelPicker) document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [showModelPicker])

  // ── File list for @ mentions ───────────────────────────────────────────────
  useEffect(() => {
    if (externalFileItems) { setAvailableFiles(externalFileItems); return }
    if (!effectiveProjectId) return
    fetch(`${apiBaseUrl}/api/v1/files/${effectiveProjectId}`)
      .then((r) => r.ok ? r.json() : [])
      .then((items) => setAvailableFiles(Array.isArray(items) ? items : []))
      .catch(() => {})
  }, [effectiveProjectId, externalFileItems, apiBaseUrl])

  // ── R129: resolve a pending tool-permission prompt (Allow / Deny / Always) ───
  // The backend's gated turn awaits POST /permission (60s, else auto-deny). The drawer is the live
  // AI surface, so it owns the response. `scope:"always"` (R111–R114) persists a learned allow rule.
  const resolvePermission = useCallback(async (decisionId: string, decision: "allow" | "deny", scope: "once" | "always" = "once") => {
    // Optimistically clear the prompt so the UI responds instantly.
    setMessages((prev) => prev.map((m) => ({
      ...m,
      toolCalls: (m.toolCalls ?? []).map((tc) => tc.decisionId === decisionId
        ? { ...tc, awaitingPermission: false, denied: decision === "deny" } : tc),
    })))
    try {
      await fetch(`${apiBaseUrl}/api/v1/agent/${effectiveProjectId}/permission`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ decision_id: decisionId, decision, scope }),
      })
    } catch { /* best-effort; the backend times out to deny if this never lands */ }
  }, [apiBaseUrl, effectiveProjectId])

  // ── Textarea resize ────────────────────────────────────────────────────────
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 192)}px`
    }
  }, [input])

  // ── Slash command filtered list ────────────────────────────────────────────
  const filteredSlash = useMemo(
    () => SLASH_COMMANDS.filter((c) => c.cmd.startsWith(slashPopup.filter) || c.description.toLowerCase().includes(slashPopup.filter.toLowerCase())).slice(0, 10),
    [slashPopup.filter]
  )

  // ── @ mention filtered list ─────────────────────────────────────────────────
  // Two kinds in one keyboard-navigable list: host-fed @-context PROVIDERS
  // (@git/@codebase/@problems/… — for discoverability) first, then matching files.
  type MentionItem =
    | { kind: "provider"; name: string; description: string; takesArg?: boolean }
    | { kind: "file"; file: FileItem }
  const mentionItems = useMemo<MentionItem[]>(() => {
    const f = mentionPopup.filter.toLowerCase()
    const provs: MentionItem[] = (contextProviders ?? [])
      .filter((p) => p.name.toLowerCase().includes(f) || p.description.toLowerCase().includes(f))
      .map((p) => ({ kind: "provider", name: p.name, description: p.description, takesArg: p.takesArg }))
    const files: MentionItem[] = availableFiles
      .filter((file) => file.name.toLowerCase().includes(f) || file.path.toLowerCase().includes(f))
      .slice(0, 8)
      .map((file) => ({ kind: "file", file }))
    return [...provs, ...files]
  }, [contextProviders, availableFiles, mentionPopup.filter])

  // ── Current model info ─────────────────────────────────────────────────────
  const currentModelInfo = useMemo(() => {
    if (selectedModel === "auto") return { id: "auto", label: "Auto", vendor: "askfinz", vendorLabel: "askFinz AI" }
    return ALL_MODELS.find((m) => m.id === selectedModel) ?? ALL_MODELS[0]
  }, [selectedModel])

  // ── Input change handler ───────────────────────────────────────────────────
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setInput(val)

    // Slash popup: trigger when line starts with /
    const lineStart = val.lastIndexOf("\n") + 1
    const currentLine = val.slice(lineStart)
    if (currentLine.startsWith("/")) {
      const filter = currentLine.slice(1)
      setSlashPopup({ open: true, filter, idx: 0 })
      setMentionPopup((p) => ({ ...p, open: false }))
    } else {
      setSlashPopup((p) => ({ ...p, open: false }))
    }

    // @ mention popup: trigger on @ followed by word chars
    const cursor = e.target.selectionStart ?? val.length
    const before = val.slice(0, cursor)
    const atMatch = before.match(/@(\w*)$/)
    if (atMatch) {
      const filter = atMatch[1]
      setMentionPopup({ open: true, filter, idx: 0, start: cursor - atMatch[0].length })
    } else {
      setMentionPopup((p) => ({ ...p, open: false }))
    }
  }, [])

  // ── Keyboard handler ───────────────────────────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashPopup.open) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSlashPopup((p) => ({ ...p, idx: Math.min(p.idx + 1, filteredSlash.length - 1) })) }
      if (e.key === "ArrowUp") { e.preventDefault(); setSlashPopup((p) => ({ ...p, idx: Math.max(p.idx - 1, 0) })) }
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault()
        const selected = filteredSlash[slashPopup.idx]
        if (selected) applySlashCommand(selected.cmd)
        return
      }
      if (e.key === "Escape") { setSlashPopup((p) => ({ ...p, open: false })); return }
    }

    if (mentionPopup.open && mentionItems.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionPopup((p) => ({ ...p, idx: Math.min(p.idx + 1, mentionItems.length - 1) })) }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionPopup((p) => ({ ...p, idx: Math.max(p.idx - 1, 0) })) }
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault()
        const selected = mentionItems[mentionPopup.idx]
        if (selected) applyMention(selected)
        return
      }
      if (e.key === "Escape") { setMentionPopup((p) => ({ ...p, open: false })); return }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }, [slashPopup, mentionPopup, filteredSlash, mentionItems])

  // ── Apply slash command ────────────────────────────────────────────────────
  const applySlashCommand = useCallback((cmd: string) => {
    setSlashPopup({ open: false, filter: "", idx: 0 })

    // Handle local UI toggles
    if (cmd === "clear") { setMessages([]); setInput(""); return }
    if (cmd === "plan") { setPlanMode((p) => !p); setInput(""); return }
    if (cmd === "confirm") { setConfirmMode((c) => !c); setInput(""); return }  // R133: gate each write/exec
    if (cmd === "think") { setThinkingMode((m) => m === "off" ? "adaptive" : "off"); setInput(""); return }
    if (cmd === "coordinator") { setCoordinatorMode((c) => !c); setInput(""); return }
    if (cmd === "review" || cmd === "diffreview") { setDiffReviewMode((d) => !d); setInput(""); return }
    if (cmd === "tts") { setTtsEnabled((t) => !t); setInput(""); return }
    if (cmd === "screenshot") { setScreenshotMode((s) => !s); setInput(""); return }
    if (cmd === "swarm") { setActiveTab("swarm"); setInput(""); return }
    if (cmd === "library") { if (typeof window !== "undefined") window.location.href = "/library"; setInput(""); return }
    if (cmd === "build") { if (typeof window !== "undefined") window.location.href = "/build"; setInput(""); return }

    // Pass remaining commands as text message to the agent
    setInput(`/${cmd} `)
    textareaRef.current?.focus()
  }, [])

  // ── Apply @ mention (provider OR file) ───────────────────────────────────────
  const applyMention = useCallback((item: MentionItem) => {
    const cursor = mentionPopup.start + (mentionPopup.filter.length + 1)
    const before = input.slice(0, mentionPopup.start)
    const after = input.slice(cursor)
    // Providers insert `@name:` (cursor parked for the arg) or `@name ` (no arg);
    // files insert the existing `@path ` token.
    const token = item.kind === "provider"
      ? `@${item.name}${item.takesArg ? ":" : " "}`
      : `@${item.file.path} `
    setInput(`${before}${token}${after}`)
    if (item.kind === "file") {
      setMentionedPaths((prev) => prev.includes(item.file.path) ? prev : [...prev, item.file.path])
    }
    setMentionPopup({ open: false, filter: "", idx: 0, start: -1 })
    textareaRef.current?.focus()
  }, [input, mentionPopup])

  // ── File attach handler ────────────────────────────────────────────────────
  // AF-14: shared attach pipeline — used by the file picker, paste, and drag-drop.
  const addFiles = useCallback((files: File[]) => {
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) continue  // skip >10MB
      const isImage = file.type.startsWith("image/")
      const reader = new FileReader()
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string
        const base64 = dataUrl.split(",")[1]
        setAttachments((prev) => [...prev, {
          type: isImage ? "image" : "document",
          media_type: file.type || "application/octet-stream",
          data: base64,
          name: file.name || (isImage ? `pasted-image.${(file.type.split("/")[1] || "png")}` : "file"),
          dataUrl,
          size: file.size,
        }])
      }
      reader.readAsDataURL(file)
    }
  }, [])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []))
    if (e.target) e.target.value = ""
  }, [addFiles])

  // AF-14: paste an image (e.g. a screenshot) directly into the composer — claude-code/opencode parity.
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.items ?? [])
      .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
      .map((it) => it.getAsFile())
      .filter((f): f is File => f != null)
    if (files.length) {
      e.preventDefault() // don't also paste the image's text/blob URL into the textarea
      addFiles(files)
    }
  }, [addFiles])

  // AF-14: drag-drop files (images/docs) onto the composer.
  const [isDragging, setIsDragging] = useState(false)
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer?.files ?? [])
    if (files.length) addFiles(files)
  }, [addFiles])

  // ── Send message ───────────────────────────────────────────────────────────
  // ── Core streaming engine ─────────────────────────────────────────────────
  const runStream = useCallback(async (
    text: string,
    historyForApi: ChatMessage[],
    currentAttachments: AttachmentItem[],
    assistantId: string,
    modelOverride?: string,
  ) => {
    const controller = new AbortController()
    abortRef.current = controller

    // @-context (optional, host-fed): resolve @mentions client-side and prepend the
    // returned block to the message. Applied ONCE here so BOTH the local-vLLM path
    // and the cloud path send the enriched prompt — no backend change. Best-effort:
    // a resolver failure or empty block leaves the message untouched.
    if (resolveContextBlock) {
      try {
        const block = await resolveContextBlock(text)
        if (block) text = `${block}\n\n${text}`
      } catch {
        /* @-context resolution must never block the turn */
      }
    }

    // Opt-in per-message grounding hook (window.__askfinzGround): a host app can
    // register a fn that, given THIS message, returns a grounding block (e.g.
    // finance retrieves relevant earnings-call / filing passages via /research
    // for the active ticker). Prepended to the API text only — the displayed
    // user bubble is unchanged. Best-effort, never blocks the turn. No-op when
    // no host registered it (every other app).
    if (typeof window !== "undefined" && typeof (window as any).__askfinzGround === "function") {
      try {
        const _g = await (window as any).__askfinzGround(text)
        if (_g && typeof _g === "string") text = `${_g}\n\n${text}`
      } catch {
        /* grounding must never block the turn */
      }
    }

    const effectiveModel = modelOverride ?? selectedModel

    // ── LOCAL inference seam (askFinz Desktop on-device vLLM) ────────────────
    // Consulted BEFORE any cloud/agent routing. If the host resolves a local
    // endpoint for this model, run the whole turn against it in OpenAI
    // chat-completions format and parse the standard OpenAI SSE
    // (`choices[].delta.content`). No auth_token / cloud body. When the host
    // passes no `resolveLocalEndpoint` (every web app) or it returns null, we
    // fall straight through to the unchanged cloud path below.
    const _localTarget = resolveLocalEndpoint?.(effectiveModel) ?? null
    if (_localTarget) {
      try {
        const res = await fetch(_localTarget.url, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(_localTarget.headers ?? {}) },
          body: JSON.stringify({
            model: effectiveModel,
            messages: [
              ...historyForApi.slice(-10).map((m) => ({ role: m.role, content: m.content })),
              { role: "user", content: text },
            ],
            stream: true,
          }),
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const reader = res.body?.getReader()
        const decoder = new TextDecoder()
        if (!reader) throw new Error("No response body")

        let buf = ""
        let finalContent = ""
        // Same RAF-batched reveal the cloud path uses for the OpenAI-delta fallback.
        const rafState = { id: 0 }
        const scheduleFlush = () => {
          if (rafState.id) return
          rafState.id = requestAnimationFrame(() => {
            rafState.id = 0
            setMessages((prev) => prev.map((m) => m.id === assistantId
              ? { ...m, content: finalContent }
              : m))
          })
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const events = buf.split("\n\n")
          buf = events.pop() ?? ""
          for (const ev of events) {
            if (!ev.trim()) continue
            const dataLine = ev.split("\n").find((l) => l.startsWith("data: "))
            if (!dataLine) continue
            if (dataLine.includes("[DONE]")) continue
            try {
              const data = JSON.parse(dataLine.slice(6))
              const delta = data?.choices?.[0]?.delta?.content ?? data?.content ?? ""
              if (delta) { finalContent += delta; scheduleFlush() }
            } catch { /* skip malformed */ }
          }
        }

        if (rafState.id) { cancelAnimationFrame(rafState.id); rafState.id = 0 }
        setMessages((prev) => prev.map((m) => m.id === assistantId
          ? { ...m, content: finalContent, isStreaming: false }
          : m))
        if (ttsEnabled && finalContent && "speechSynthesis" in window) {
          window.speechSynthesis.speak(new SpeechSynthesisUtterance(finalContent.slice(0, 500)))
        }
      } catch (err: any) {
        if (err?.name === "AbortError") {
          setMessages((prev) => prev.map((m) => m.id === assistantId
            ? { ...m, content: m.content || "Message cancelled.", isStreaming: false }
            : m))
        } else {
          const errMsg = err instanceof Error ? err.message : String(err)
          setMessages((prev) => prev.map((m) => m.id === assistantId
            ? { ...m, content: `Local model error — ${errMsg}`, isStreaming: false }
            : m))
        }
      } finally {
        setIsLoading(false)
        abortRef.current = null
      }
      return
    }

    try {
      const useAgent = !!effectiveProjectId

      const endpoint = useAgent
        ? `${apiBaseUrl}/api/v1/agent/${effectiveProjectId}/chat`
        : `${apiBaseUrl}/api/chat/completions`

      // Routing maps + resolveRoute() live in ../data/provider-models (single
      // source of truth, shared with the catalog API + browser extension).
      const _modelEntry = PROVIDER_MODELS.find(m => m.id === effectiveModel)
        ?? anthropicModels.find(m => m.id === effectiveModel)
        ?? freeModels.find(m => m.id === effectiveModel)
      let routeProvider: string
      let routeModel: string
      if (!_modelEntry || effectiveModel === "auto") {
        // Auto mode → orchestrator handles routing (think/plan/route pipeline)
        routeProvider = "auto"
        routeModel    = "auto"
        setAutoRouteLabel("Orchestrator…")
        setOrchPhase("")
        setMessages(prev => prev.map(m =>
          m.id === assistantId ? { ...m, model: "auto:Orchestrator" } : m
        ))
      } else if (_modelEntry.provider === "anthropic") {
        // Live Claude ids go to the Anthropic provider as-is (the backend falls back
        // to other providers when the monthly credit is at its stop line).
        routeProvider = "anthropic"; routeModel = effectiveModel
      } else if (_modelEntry.provider === "openrouter") {
        // Free OpenRouter models (runtime-loaded, not in the static catalog) —
        // model ID is already the full OpenRouter model ID.
        routeProvider = "openrouter"; routeModel = effectiveModel
      } else {
        // Catalog models — resolveRoute reproduces the exact askfinz/bedrock +
        // provider-route + id-override mapping this block used to inline.
        ;({ provider: routeProvider, model: routeModel } = resolveRoute(effectiveModel))
      }

      const payload = useAgent
        ? {
            message: text,
            model: effectiveModel,
            session_id: sessionId,
            thinking_mode: thinkingMode,
            coordinator_mode: coordinatorMode,
            plan_mode: planMode,
            require_confirmation: confirmMode,  // R133: backend gates write/exec → permission_request → Allow/Deny/Always (R129)
            at_mentions: mentionedPaths,
            attachments: currentAttachments.map((a) => ({
              type: a.type, media_type: a.media_type, data: a.data, name: a.name,
            })),
          }
        : (() => {
            const _tok = getAuthToken?.() ?? null
            const _cardCtx = (() => {
              try {
                const custom = JSON.parse(localStorage.getItem("askfinz_custom_cards") || "[]")
                return getSmartCardContext(text, custom)
              } catch { return "" }
            })()
            // Per-pane context (LF-C01): the host app's `metadata()` returns what the
            // user is *looking at* — which pane + its url/title/pageText/file/etc. The
            // prop existed but was never sent (dead), so pane context never reached the
            // LLM. Send it BOTH as a structured `client_context` field (for backends
            // that read it) AND as a leading system message so the model is grounded
            // even without backend support. No-op when metadata() is absent/empty (web).
            const _paneMeta = (() => {
              try { const m = metadata?.() ?? {}; return m && Object.keys(m).length ? m : null }
              catch { return null }
            })()
            const _paneCtxMsg = _paneMeta
              ? [{ role: "system", content: "[askFinz Desktop — active pane context. The user is currently interacting with the '" + String((_paneMeta as any).service ?? (_paneMeta as any).pane ?? "app") + "' pane; use this to ground your answer about what they are viewing/working on]\n" + JSON.stringify(_paneMeta) }]
              : []
            return {
              messages: [..._paneCtxMsg, ...historyForApi.slice(-10).map((m) => ({ role: m.role, content: m.content })), { role: "user", content: text }],
              model: routeModel,
              provider: routeProvider,
              app: appName,
              stream: true,
              attachments: currentAttachments.map((a) => ({
                type: a.type, media_type: a.media_type, data: a.data, name: a.name,
              })),
              ...(_paneMeta ? { client_context: _paneMeta } : {}),
              ...(_cardCtx ? { card_library: _cardCtx } : {}),
              ...(_tok ? {
                auth_token: _tok,
                conversation_id: conversationIdRef.current,
                source_app: sourceApp,
                turn_index: historyForApi.length,
              } : {}),
            }
          })()

      // Model fallback chain: when a provider returns 503/429 (overloaded
       // or rate-limited), transparently retry with a sibling model from a
       // different provider. We try up to 3 alternatives before surfacing
       // the error. Order: primary → openrouter Sonnet → groq Llama →
       // google Gemini Flash → Cohere Command.
      const _fallbackChain: { provider: string; model: string }[] = [
        { provider: routeProvider, model: routeModel },
        { provider: "openrouter",        model: "anthropic/claude-sonnet-4-6" },
        { provider: "groq",              model: "llama-3.3-70b-versatile" },
        { provider: "google_ai_studio",  model: "gemini-2.5-flash" },
        { provider: "cohere",            model: "command-a-03-2025" },
      ].filter((v, i, arr) => arr.findIndex(x => x.provider === v.provider && x.model === v.model) === i)

      let res: Response | null = null
      let _lastErr: any = null
      let _usedAttempt = 0
      for (let i = 0; i < _fallbackChain.length; i++) {
        const attempt = _fallbackChain[i]
        const attemptPayload = useAgent
          ? payload
          : { ...payload, provider: attempt.provider, model: attempt.model }
        try {
          const r = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(attemptPayload),
            signal: controller.signal,
            // chat.askfinz.ai verifies the session server-side (HttpOnly cookie) and
            // bills the turn, so the cookie must ride along on this cross-origin call.
            // Only from our own web origins: askFinz Desktop (file://) authenticates
            // with the body/Bearer token instead, and a credentialed request from a
            // non-askfinz origin would just fail CORS.
            credentials: (typeof window !== "undefined" && /(^|\.)askfinz\.ai$/i.test(window.location.hostname)) ? "include" : "same-origin",
          })
          if (r.ok) { res = r; _usedAttempt = i; break }
          // Only fall through on transient/retryable errors. 4xx other
          // than 429 = real client error, surface it.
          if (![429, 502, 503, 504].includes(r.status)) {
            res = r; _usedAttempt = i; break
          }
          _lastErr = new Error(`HTTP ${r.status}`)
          // Surface the rotation in the assistant bubble so the user knows.
          if (i < _fallbackChain.length - 1) {
            const next = _fallbackChain[i + 1]
            setMessages(prev => prev.map(m => m.id === assistantId
              ? { ...m, content: `(${attempt.provider} returned ${r.status} — falling back to ${next.provider})\n\n` }
              : m))
          }
        } catch (e) { _lastErr = e }
      }
      if (!res) throw (_lastErr || new Error("All model fallbacks failed"))
      // 401 = not signed in (or session expired); 402 = out of credits. Both are
      // the user's state, not a fault, so say what to do in plain words.
      if (res.status === 401 || res.status === 402) {
        const friendly = res.status === 401
          ? "Please sign in to chat with askFinz. Your session may have expired — sign in again and resend your message."
          : "You are out of credits. Top up or upgrade your plan in your account settings to keep chatting."
        setMessages((prev) => prev.map((m) => m.id === assistantId
          ? { ...m, content: friendly, isStreaming: false }
          : m))
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      void _usedAttempt

      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      if (!reader) throw new Error("No response body")

      let buf = ""
      let finalContent = ""
      const thoughtStart = Date.now()
      let thinkingActive = false
      let thinkingAccumulated = ""
      let usageData: { inputTokens?: number; outputTokens?: number; cachedTokens?: number } = {}

      // RAF buffer: batch text UI updates to ~60fps with a 200ms initial buffer for smooth reveal
      const rafState = { id: 0, content: "", thinking: "" }
      const streamStart = Date.now()
      const scheduleFlush = () => {
        if (rafState.id) return
        const flush = () => {
          rafState.id = 0
          setMessages((prev) => prev.map((m) => m.id === assistantId
            ? { ...m, content: rafState.content, thinkingContent: rafState.thinking || undefined, thoughtTimeMs: thinkingActive ? undefined : (Date.now() - thoughtStart) }
            : m))
        }
        if (Date.now() - streamStart < 200) {
          rafState.id = requestAnimationFrame(() => { rafState.id = requestAnimationFrame(flush) })
        } else {
          rafState.id = requestAnimationFrame(flush)
        }
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const events = buf.split("\n\n")
        buf = events.pop() ?? ""

        for (const ev of events) {
          if (!ev.trim()) continue
          const dataLine = ev.split("\n").find((l) => l.startsWith("data: "))
          if (!dataLine) continue
          if (dataLine.includes("[DONE]")) continue

          try {
            const data = JSON.parse(dataLine.slice(6))

            if (data.type === "session") {
              setSessionId(data.session_id)
            } else if (data.type === "text" && data.content) {
              finalContent += data.content
              if (!thinkingActive && finalContent.includes("<think>")) thinkingActive = true
              if (thinkingActive && finalContent.includes("</think>")) thinkingActive = false
              rafState.content = finalContent
              scheduleFlush()
            } else if (data.type === "thinking" && data.content) {
              thinkingAccumulated += data.content
              rafState.thinking = thinkingAccumulated
              scheduleFlush()
            } else if (data.type === "tool_call") {
              // AF-13: the backend emits `tool_call{id,name,input}` (the request, before execution) —
              // this is where the tool args live. In diff-review mode, snapshot the file's CURRENT
              // content (old) keyed by id so we can diff old→new on the matching tool_result.
              const tname = data.name ?? data.tool_name
              if (diffReviewModeRef.current && effectiveProjectId &&
                  (tname === "write_file" || tname === "edit_file") && data.input?.path) {
                const p = data.input.path as string
                const tid = data.id ?? data.tool_id
                const tok = getAuthToken?.() ?? null
                fetch(`${apiBaseUrl}/api/v1/files/${effectiveProjectId}/read?path=${encodeURIComponent(p)}`, {
                  headers: tok ? { Authorization: `Bearer ${tok}` } : {},
                }).then((r) => (r.ok ? r.json() : null)).then((d) => {
                  preWriteSnapshotsRef.current.set(tid, { path: p, oldContent: (d?.content as string) ?? "" })
                }).catch(() => { preWriteSnapshotsRef.current.set(tid, { path: p, oldContent: "" }) })
              }
            } else if (data.type === "tool_start") {
              const tc: ToolCallRecord = { id: data.id ?? data.tool_id, name: data.name ?? data.tool_name, input: data.input ?? {}, running: true, expanded: true }
              setMessages((prev) => prev.map((m) => m.id === assistantId
                ? { ...m, toolCalls: [...(m.toolCalls ?? []), tc] }
                : m))
            } else if (data.type === "tool_result") {
              const tid = data.id ?? data.tool_id
              setMessages((prev) => prev.map((m) => m.id === assistantId
                ? { ...m, toolCalls: (m.toolCalls ?? []).map((tc) => tc.id === tid ? { ...tc, result: data.result, running: false } : tc) }
                : m))
              // AF-13: build a pending per-hunk diff from the pre-write snapshot + the now-written content.
              {
                const snap = preWriteSnapshotsRef.current.get(tid)
                if (snap && effectiveProjectId) {
                  preWriteSnapshotsRef.current.delete(tid)
                  const tok = getAuthToken?.() ?? null
                  fetch(`${apiBaseUrl}/api/v1/files/${effectiveProjectId}/read?path=${encodeURIComponent(snap.path)}`, {
                    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
                  }).then((r) => (r.ok ? r.json() : null)).then((d) => {
                    const newContent = (d?.content as string) ?? ""
                    if (newContent !== snap.oldContent) {
                      setPendingDiffs((prev) => [...prev, {
                        id: `${tid}-${prev.length}`,
                        path: snap.path,
                        oldContent: snap.oldContent,
                        newContent,
                        description: "Modified by agent",
                      }])
                    }
                  }).catch(() => null)
                }
              }
            } else if (data.type === "tool_diff") {
              setMessages((prev) => prev.map((m) => m.id === assistantId
                ? { ...m, toolCalls: (m.toolCalls ?? []).map((tc) => tc.id === data.tool_id ? { ...tc, diff: data.diff } : tc) }
                : m))
            } else if (data.type === "permission_request") {
              // R129: backend sends `decision_id` (== the tool id), NOT `tool_id` — the old match
              // never fired, so the approval prompt was invisible + unresolvable. Match decision_id
              // and capture danger so the card can show Allow/Deny/(Always) buttons.
              setMessages((prev) => prev.map((m) => m.id === assistantId
                ? { ...m, toolCalls: (m.toolCalls ?? []).map((tc) => tc.id === data.decision_id ? { ...tc, awaitingPermission: true, decisionId: data.decision_id, danger: Boolean(data.danger) } : tc) }
                : m))
            } else if (data.type === "planning") {
              setOrchPhase("Planning…")
            } else if (data.type === "routing") {
              setOrchPhase("Routing…")
              if (data.assignments) {
                const first = Object.values(data.assignments as Record<string, string>)[0] ?? ""
                const modelPart = first.split(" → ")[1]?.split("/").slice(-1)[0] ?? first
                if (modelPart) {
                  setAutoRouteLabel(modelPart)
                  setMessages(prev => prev.map(m =>
                    m.id === assistantId ? { ...m, model: `auto:${modelPart}` } : m
                  ))
                }
              }
            } else if (data.type === "reflecting") {
              setOrchPhase("Reflecting…")
            } else if (data.type === "rag") {
              setOrchPhase("Reading context…")
            } else if (data.type === "done") {
              setOrchPhase("")
              if (data.usage) {
                const u = data.usage
                usageData = {
                  inputTokens:  u.input_tokens  ?? u.prompt_tokens     ?? undefined,
                  outputTokens: u.output_tokens ?? u.completion_tokens ?? undefined,
                  cachedTokens: u.cache_read_input_tokens               ?? undefined,
                }
              }
              break
            } else if (data.usage) {
              const u = data.usage
              usageData = { inputTokens: u.prompt_tokens ?? undefined, outputTokens: u.completion_tokens ?? undefined }
              const delta = data?.choices?.[0]?.delta?.content ?? ""
              if (delta) { finalContent += delta; rafState.content = finalContent; scheduleFlush() }
            } else {
              const delta = data?.choices?.[0]?.delta?.content ?? data?.content ?? ""
              if (delta) {
                finalContent += delta
                rafState.content = finalContent
                scheduleFlush()
              }
            }
          } catch { /* skip malformed */ }
        }
      }

      // Cancel pending RAF and do a final synchronous flush with full content
      if (rafState.id) { cancelAnimationFrame(rafState.id); rafState.id = 0 }
      setMessages((prev) => prev.map((m) => m.id === assistantId
        ? { ...m, content: finalContent, thinkingContent: thinkingAccumulated || undefined, isStreaming: false, ...usageData }
        : m))

      if (ttsEnabled && finalContent && "speechSynthesis" in window) {
        const utt = new SpeechSynthesisUtterance(finalContent.slice(0, 500))
        window.speechSynthesis.speak(utt)
      }
    } catch (err: any) {
      if (err?.name === "AbortError") {
        setMessages((prev) => prev.map((m) => m.id === assistantId
          ? { ...m, content: m.content || "Message cancelled.", isStreaming: false }
          : m))
        return
      }
      const errMsg = err instanceof Error ? err.message : String(err)
      setMessages((prev) => prev.map((m) => m.id === assistantId
        ? { ...m, content: `Sorry, something went wrong — ${errMsg}`, isStreaming: false }
        : m))
    } finally {
      setIsLoading(false)
      abortRef.current = null
      setOrchPhase("")
    }
  }, [selectedModel, effectiveProjectId, apiBaseUrl, appName, sessionId, thinkingMode, coordinatorMode, planMode, confirmMode, mentionedPaths, ttsEnabled, getAuthToken, sourceApp, resolveLocalEndpoint, anthropicModels, freeModels])

  const sendMessage = useCallback(async () => {
    const text = input.trim()
    if (!text || isLoading) return

    // Plan-mode hand-off: when the user is in the research writer with
    // Plan toggled ON, send the prompt to the writer's plan-mode flow
    // (Cursor-style Q&A → plan → execute) instead of streaming the AI
    // reply directly. The drawer logs a brief confirmation message so
    // the conversation history remains continuous.
    if (planMode && typeof window !== "undefined" && (window as any).__researchEditor) {
      setInput("")
      if (textareaRef.current) {
        textareaRef.current.value = ""
        textareaRef.current.style.height = "auto"
      }
      setMentionedPaths([])
      const userMsg: ChatMessage = {
        id: generateId(), role: "user", content: text, timestamp: new Date(),
      }
      const sysMsg: ChatMessage = {
        id: generateId(), role: "assistant", timestamp: new Date(), isStreaming: false,
        content: "Plan mode — I'll ask a few clarifying questions in the writer, then propose an ordered plan you can approve before I write anything.",
      }
      setMessages(prev => [...prev, userMsg, sysMsg])
      window.dispatchEvent(new CustomEvent("research:plan-start", {
        detail: { prompt: text, workflowName: "Research Paper" },
      }))
      return
    }

    // Handle /remember and /forget before LLM
    if (getAuthToken && (text.startsWith("/remember ") || text.startsWith("/forget "))) {
      const isRemember = text.startsWith("/remember ")
      const content = text.slice(isRemember ? 10 : 8).trim()
      if (content) {
        setInput("")
        setMentionedPaths([])
        const sysId = generateId()
        const userMsg: ChatMessage = { id: generateId(), role: "user", content: text, timestamp: new Date() }
        const sysMsg: ChatMessage = { id: sysId, role: "assistant", content: "", timestamp: new Date(), isStreaming: true }
        setMessages(prev => [...prev, userMsg, sysMsg])
        setIsLoading(true)
        try {
          const tok = getAuthToken()
          if (!tok) throw new Error("Not authenticated")
          const endpoint = isRemember ? `${apiBaseUrl}/api/memory/remember` : `${apiBaseUrl}/api/memory/forget`
          const body = isRemember ? { content, source_app: sourceApp } : { key: content }
          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: JSON.stringify(body),
          })
          const data = await res.json()
          let reply = res.ok
            ? isRemember
              ? `Memory saved — **${data.category} / ${data.key}**: ${data.value}`
              : `Forgotten: "${data.key}"`
            : "Memory service unavailable."
          setMessages(cur => cur.map(m => m.id === sysId ? { ...m, content: reply, isStreaming: false } : m))
        } catch (e) {
          setMessages(cur => cur.map(m => m.id === sysId ? { ...m, content: `Memory error: ${e}`, isStreaming: false } : m))
        } finally { setIsLoading(false) }
        return
      }
    }

    // /askdocs slash command — RAG against the public library, then run
    // the LLM with the retrieved chunks as context. Two-step billed flow
    // per LIBRARY_MONETIZATION_PLAN.md §13.3 / R6.
    if (getAuthToken && text.startsWith("/askdocs ")) {
      const query = text.slice(9).trim()
      if (query) {
        setInput("")
        if (textareaRef.current) {
          textareaRef.current.value = ""
          textareaRef.current.style.height = "auto"
        }
        setMentionedPaths([])
        setShowModelPanel(false)
        const userMsg: ChatMessage = {
          id: generateId(), role: "user", content: text, timestamp: new Date(),
        }
        const assistantId = generateId()
        const assistantMsg: ChatMessage = {
          id: assistantId, role: "assistant", content: "📚 Searching the public library…",
          timestamp: new Date(), isStreaming: true, model: selectedModel, toolCalls: [],
        }
        setMessages(prev => [...prev, userMsg, assistantMsg])
        setIsLoading(true)
        try {
          const tok = getAuthToken()
          if (!tok) throw new Error("Not authenticated")
          const researchBase = getResearchApiUrl()
          // Step 1 — estimate (free preview, no transfers)
          const estResp = await fetch(`${researchBase}/api/library/query/estimate`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ query, top_k: 5 }),
          })
          if (!estResp.ok) {
            throw new Error(`estimate failed (${estResp.status} ${estResp.statusText})`)
          }
          const est = await estResp.json()
          const estCost = Number(est?.total_charge ?? 0)
          const estChunks = Array.isArray(est?.chunks) ? est.chunks.length : 0
          if (estChunks === 0 || estCost === 0) {
            const reason = estChunks === 0
              ? "no matching documents found in the public library."
              : "all relevant chunks already owned (no fresh charge)."
            setMessages(cur => cur.map(m => m.id === assistantId
              ? { ...m, content: `📚 ${reason}`, isStreaming: false }
              : m))
            return
          }
          // Step 2 — billed retrieval
          const qResp = await fetch(`${researchBase}/api/library/query`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ query, top_k: 5, session_id: sessionId }),
          })
          if (qResp.status === 402) {
            setMessages(cur => cur.map(m => m.id === assistantId
              ? { ...m, content: `📚 Insufficient credits — needed ${estCost} cr for ${estChunks} chunk${estChunks === 1 ? "" : "s"}. Top up and try again.`, isStreaming: false }
              : m))
            return
          }
          if (qResp.status === 429) {
            setMessages(cur => cur.map(m => m.id === assistantId
              ? { ...m, content: "📚 Rate limit hit (max 100 library queries/hour). Please wait and retry.", isStreaming: false }
              : m))
            return
          }
          if (!qResp.ok) {
            throw new Error(`query failed (${qResp.status} ${qResp.statusText})`)
          }
          const data = await qResp.json()
          const chunksTxt = data?.chunks_text ?? ""
          const charged = Number(data?.total_charged ?? 0)
          const transfers = Array.isArray(data?.transfers) ? data.transfers : []
          const nChunks = transfers.length
          const docIds = Array.from(new Set(transfers.map((t: any) => t.document_id))).slice(0, 3)
          const costLine = `📚 Charged **${charged} cr** for ${nChunks} library chunk${nChunks === 1 ? "" : "s"}` +
            (docIds.length > 0 ? ` from ${docIds.length} document${docIds.length === 1 ? "" : "s"}` : "") + `.\n\n`
          // Stream the LLM with the retrieved context prepended to the prompt
          const augmented = `Use the following passages from the AskFinz public library to answer the user's question. ` +
            `Cite passages by chunk_id when relevant.\n\n` +
            `=== Library context (${nChunks} chunks) ===\n${chunksTxt}\n=== End of context ===\n\n` +
            `User question: ${query}`
          setMessages(cur => cur.map(m => m.id === assistantId
            ? { ...m, content: costLine, isStreaming: true }
            : m))
          // Run the LLM with the augmented prompt — runStream replaces
          // the assistant content, so re-prepend cost line afterwards.
          await runStream(augmented, messages, [], assistantId)
          setMessages(cur => cur.map(m => m.id === assistantId
            ? { ...m, content: costLine + (m.content || "") }
            : m))
        } catch (e) {
          const errMsg = e instanceof Error ? e.message : String(e)
          setMessages(cur => cur.map(m => m.id === assistantId
            ? { ...m, content: `📚 Library query error: ${errMsg}`, isStreaming: false }
            : m))
        } finally {
          setIsLoading(false)
        }
        return
      }
    }

    setInput("")
    // Belt-and-braces: clear the DOM value too in case anything (IME,
    // external setter that bypassed React onChange) put the textarea out
    // of sync with React state.
    if (textareaRef.current) {
      textareaRef.current.value = ""
      textareaRef.current.style.height = "auto"
    }
    setMentionedPaths([])
    setShowModelPanel(false)
    const currentAttachments = [...attachments]
    setAttachments([])
    trackModelUse(selectedModel)

    const userMsg: ChatMessage = {
      id: generateId(), role: "user", content: text, timestamp: new Date(),
      attachments: currentAttachments.length > 0 ? currentAttachments : undefined,
    }
    const assistantId = generateId()
    const assistantMsg: ChatMessage = {
      id: assistantId, role: "assistant", content: "", timestamp: new Date(),
      isStreaming: true, model: selectedModel, toolCalls: [],
    }

    setMessages((prev) => [...prev, userMsg, assistantMsg])
    setIsLoading(true)

    // Failover chain: when the active model returns an upstream error
    // (402 payment / 429 rate-limit / 503 unavailable), retry the same
    // prompt with the next model in this chain. The order favours
    // free-tier Gemini, then OpenRouter Claude tiers.
    const FAILOVER: string[] = [
      "gemini-3-flash-preview",
      "gemini-2.5-flash",
      "askfinz:fast",
      "askfinz:balanced",
      "askfinz:pro",
    ]
    const isUpstreamErr = (s: string) =>
      /(⚠️|Server error '5\d\d|Client error '4(02|29)|Payment Required|Service Unavailable|Too Many Requests)/i.test(s || "")

    let usedModel = selectedModel
    await runStream(text, messages, currentAttachments, assistantId)

    // After the stream resolves, peek at the assistant message we just wrote.
    // If it's an error frame, walk the FAILOVER chain.
    for (const candidate of FAILOVER) {
      if (candidate === usedModel) continue
      // Read latest content directly from React's pending state via a setter trick.
      let currentContent = ""
      setMessages(prev => {
        const m = prev.find(x => x.id === assistantId)
        currentContent = m?.content || ""
        return prev
      })
      if (!isUpstreamErr(currentContent)) break
      // Reset the assistant message and try the next model.
      setMessages(prev => prev.map(m => m.id === assistantId
        ? { ...m, content: "", isStreaming: true, model: candidate }
        : m))
      await new Promise(r => setTimeout(r, 400))
      usedModel = candidate
      await runStream(text, messages, currentAttachments, assistantId, candidate)
    }
  }, [input, isLoading, messages, selectedModel, attachments, runStream])

  const handleEditSubmit = useCallback(async (msgId: string, newContent: string) => {
    const trimmed = newContent.trim()
    if (!trimmed || isLoading) return

    const idx = messages.findIndex((m) => m.id === msgId)
    if (idx === -1) return

    const priorHistory = messages.slice(0, idx)
    const userMsg: ChatMessage = { id: generateId(), role: "user", content: trimmed, timestamp: new Date() }
    const assistantId = generateId()
    const assistantMsg: ChatMessage = {
      id: assistantId, role: "assistant", content: "", timestamp: new Date(),
      isStreaming: true, model: selectedModel, toolCalls: [],
    }

    setEditingId(null)
    setMessages([...priorHistory, userMsg, assistantMsg])
    setIsLoading(true)

    await runStream(trimmed, priorHistory, [], assistantId)
  }, [isLoading, messages, selectedModel, runStream])

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort()
    setOrchPhase("")
    // Gap 4: signal orchestrator to cancel via Redis abort channel
    const cid = conversationIdRef.current
    if (cid) {
      // SECURITY V-105: abort needs the signed-in user (it only stops their own stream),
      // and this call is usually cross-origin (drawer on any app -> chat.askfinz.ai).
      const tok = getAuthToken?.() ?? null
      fetch(`${apiBaseUrl}/api/orchestrator/orchestrate/abort`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ conversation_id: cid }),
      }).catch(() => {})
    }
  }, [apiBaseUrl, getAuthToken])

  // ── Swarm broadcast ────────────────────────────────────────────────────────
  const broadcastToSwarm = useCallback(() => {
    if (!swarmInput.trim()) return
    setSwarmBroadcast(swarmInput.trim())
    setSwarmInput("")
    setTimeout(() => setSwarmBroadcast(""), 100)
  }, [swarmInput])

  // ── Suggestions ────────────────────────────────────────────────────────────
  // Empty-state suggestion chips. Host apps may override per-context (e.g. the
  // askFinz Desktop passes pane-specific actions for Browser/IDE/CLI/Storage);
  // when omitted, the default discovery prompts are shown (web parity).
  const suggestions = useMemo(
    () =>
      suggestionsProp && suggestionsProp.length
        ? suggestionsProp
        : [
            "What can I search for on askFinz?",
            "How does the mini-browser work?",
            "Show me recent research topics",
            "Summarize the latest news",
          ],
    [suggestionsProp],
  )

  const drawerWidth = isOpen ? width : 0

  // ── Status badges ──────────────────────────────────────────────────────────
  const activeModes = [
    planMode && "Plan",
    confirmMode && "Confirm",
    coordinatorMode && "Coord",
    thinkingMode !== "off" && "Think",
    ttsEnabled && "TTS",
    screenshotMode && "Screenshot",
  ].filter(Boolean) as string[]

  // ── AF-13: per-hunk diff-review apply/revert handlers ──
  const writeReviewedFile = async (path: string, content: string) => {
    try {
      const tok = getAuthToken?.() ?? null
      const res = await fetch(`${apiBaseUrl}/api/v1/files/${effectiveProjectId}/write`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ path, content }),
      })
      if (!res.ok) throw new Error(`write ${res.status}`)
    } catch { /* surfaced by the file tree on next read */ }
  }
  const applyReviewedDiff = (id: string, resolvedContent: string) => {
    const d = pendingDiffs.find((x) => x.id === id)
    if (d) void writeReviewedFile(d.path, resolvedContent)
    setPendingDiffs((prev) => prev.filter((x) => x.id !== id))
  }
  const rejectReviewedDiff = (id: string) => {
    const d = pendingDiffs.find((x) => x.id === id)
    if (d) void writeReviewedFile(d.path, d.oldContent) // discard → restore original
    setPendingDiffs((prev) => prev.filter((x) => x.id !== id))
  }
  const applyAllReviewed = (resolved: Array<{ id: string; content: string }>) => {
    for (const r of resolved) {
      const d = pendingDiffs.find((x) => x.id === r.id)
      if (d) void writeReviewedFile(d.path, r.content)
    }
    setPendingDiffs([])
  }
  const rejectAllReviewed = () => {
    for (const d of pendingDiffs) void writeReviewedFile(d.path, d.oldContent)
    setPendingDiffs([])
  }
  // Debug hooks: seed a mock pending diff, and toggle diff-review capture mode (for e2e tests where
  // the slash-popup path isn't drivable synthetically). Both only set local UI state.
  useEffect(() => {
    const w = window as unknown as {
      __afzSeedDiffReview?: (d: unknown) => void
      __afzSetDiffReview?: (v: boolean) => void
    }
    w.__afzSeedDiffReview = (d) => setPendingDiffs(d as never)
    w.__afzSetDiffReview = (v) => setDiffReviewMode(!!v)
  }, [])

  return (
    <>
      {pendingDiffs.length > 0 && (
        <DiffReviewModal
          diffs={pendingDiffs}
          onApply={applyReviewedDiff}
          onReject={rejectReviewedDiff}
          onApplyAll={applyAllReviewed}
          onRejectAll={rejectAllReviewed}
        />
      )}
      <VoiceCallDialog
        open={showCallDialog}
        onClose={() => setShowCallDialog(false)}
        model={selectedModel}
        getAuthToken={getAuthToken}
      />
      <style>{`
        .askfinz-chat-panel {
          ${isFullScreen
            ? "position: absolute; inset: 0; pointer-events: auto;"
            : `position: fixed;
          top: 4rem;
          bottom: 2.75rem;
          ${isRight ? "right: 0" : "left: 0"};
          z-index: 200;
          width: ${drawerWidth}px;
          min-width: ${drawerWidth}px;
          overflow: hidden;
          transition: width 0.35s cubic-bezier(0.4,0,0.2,1), min-width 0.35s cubic-bezier(0.4,0,0.2,1);
          pointer-events: ${isOpen ? "auto" : "none"};
          border-${isRight ? "left" : "right"}: 1px solid var(--border-color, rgba(255,255,255,0.07));
          box-shadow: ${isOpen ? (isRight ? "-10px 0 30px rgba(0,0,0,0.4)" : "10px 0 30px rgba(0,0,0,0.4)") : "none"};`
          }
          background: var(--bg-main, #0a0a0a);
          display: flex;
          flex-direction: column;
        }
        .askfinz-chat-toggle {
          position: fixed;
          ${isRight ? `right: ${isOpen ? width - 32 : 0}px` : `left: ${isOpen ? width - 32 : 0}px`};
          top: 50%;
          transform: translateY(-50%);
          z-index: 201;
          pointer-events: auto;
          transition: right 0.35s cubic-bezier(0.4,0,0.2,1), left 0.35s cubic-bezier(0.4,0,0.2,1);
        }
        .askfinz-invisible-scrollbar::-webkit-scrollbar { display: none; }
        .askfinz-invisible-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes askfinz-fade-in-up {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .askfinz-msg-enter { animation: askfinz-fade-in-up 0.2s ease forwards; }
        @keyframes askfinz-rgb-wave {
          0% { background-position: 200% 50%; }
          100% { background-position: 0% 50%; }
        }
        .askfinz-rgb-icon {
          background: linear-gradient(90deg, #ff0000, #ff7f00, #ffff00, #00ff00, #00ffff, #ff00ff, #ff0000);
          background-size: 200% 100%;
          animation: askfinz-rgb-wave 12s linear infinite;
          -webkit-mask-size: contain;
          -webkit-mask-repeat: no-repeat;
          -webkit-mask-position: center;
          mask-size: contain;
          mask-repeat: no-repeat;
          mask-position: center;
        }
      `}</style>

      {/* ── Toggle Button ─────────────────────────────────────────────────── */}
      {!isFullScreen && <div className="askfinz-chat-toggle">
        <button
          id="askfinz-chat-drawer-toggle"
          onClick={() => setIsOpen((o) => !o)}
          title={isOpen ? "Close AI Assistant" : "Open AI Assistant"}
          className={`flex items-center justify-center group transition-all duration-300 ${
            isRight
              ? (!isOpen ? "rounded-l-xl rounded-r-none border-r-0" : "rounded-r-xl rounded-l-none border-l-0")
              : (!isOpen ? "rounded-r-xl rounded-l-none border-l-0" : "rounded-l-xl rounded-r-none border-r-0")
          } w-8 h-14 border border-border-color bg-[#2a2a2a] hover:bg-[#333333] shadow-xl`}
          style={{ pointerEvents: "auto" }}
        >
          {isOpen ? (
            isRight
              ? <ChevronRight size={16} className="text-gray-300 group-hover:text-white transition-colors" />
              : <ChevronLeft size={16} className="text-gray-300 group-hover:text-white transition-colors" />
          ) : (
            <div
              className="h-[15px] w-[15px] opacity-90 group-hover:opacity-100 transition-opacity askfinz-rgb-icon"
              style={{
                WebkitMaskImage: `url(${isDarkTheme ? "/shared/logos/header/AskFinz_symbol_light.svg" : "/shared/logos/header/AskFinz_symbol_black.svg"})`,
                maskImage: `url(${isDarkTheme ? "/shared/logos/header/AskFinz_symbol_light.svg" : "/shared/logos/header/AskFinz_symbol_black.svg"})`,
              }}
            />
          )}
        </button>
      </div>}

      {/* ── Drawer Panel ──────────────────────────────────────────────────── */}
      <div className="askfinz-chat-panel relative">
        {/* Anonymous login wall (public apps). Server-side endpoints also 401
            anonymous callers, so removing this overlay in devtools gains nothing. */}
        {isOpen && !aiAuthed && (
          <AiLoginOverlay subtitle="Sign in to chat with askFinz across your apps." />
        )}
        <div
          className="flex flex-col h-full w-full overflow-hidden"
          style={{
            ...(isFullScreen ? {} : { width, minWidth: width }),
            background: "hsl(var(--af-chat-panel-bg, var(--card)))",
            ...(isOpen && !aiAuthed ? { filter: "blur(4px)", pointerEvents: "none" as const, userSelect: "none" as const } : {}),
          }}
        >
          {/* Active mode badges — shown only when a mode is enabled */}
          {(activeModes.length > 0 || (orchPhase && isLoading)) && (
            <div className="shrink-0 flex gap-1 px-3 py-1.5 flex-wrap items-center" style={{ borderBottom: "1px solid hsl(var(--border) / 0.3)" }}>
              {activeModes.map((mode) => (
                <span key={mode} className="px-1.5 py-0.5 rounded-full bg-accent/15 text-accent text-[9px] font-bold uppercase tracking-wide">{mode}</span>
              ))}
              {orchPhase && isLoading && (
                <span className="px-1.5 py-0.5 rounded-full bg-accent/10 text-accent text-[9px] font-medium italic">{orchPhase}</span>
              )}
            </div>
          )}

          {/* Chat/Swarm tab bar — only shown when a project is active */}
          {effectiveProjectId && (
            <div className="shrink-0 flex" style={{ borderBottom: "1px solid hsl(var(--border) / 0.4)" }}>
              {[
                { id: "chat", label: "Chat", icon: MessageSquare },
                { id: "swarm", label: "Swarm", icon: LayoutGrid },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as "chat" | "swarm")}
                  className={`flex items-center gap-1.5 flex-1 justify-center py-1.5 text-[10px] font-semibold transition-colors border-b-2 ${
                    activeTab === id
                      ? "text-accent border-accent"
                      : "text-text-secondary border-transparent hover:text-text-primary"
                  }`}
                >
                  <Icon size={11} />
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* ── Chat Tab ──────────────────────────────────────────────────── */}
          {activeTab === "chat" && (
            <>
              {/* Model picker panel — slides in over messages area */}
              {showModelPanel && (
                <ModelPickerPanel
                  selectedModel={selectedModel}
                  onSelect={(id) => { setSelectedModel(id); setShowModelPanel(false); setAutoRouteLabel(null) }}
                  onClose={() => setShowModelPanel(false)}
                  freeModels={[...anthropicModels, ...freeModels]}
                  freeModelsLoading={freeModelsLoading}
                  localModels={localProviderModels}
                />
              )}

              {/* Messages */}
              <div className={`flex-1 overflow-y-auto p-4 askfinz-invisible-scrollbar ${showModelPanel ? "hidden" : ""}`}>
                {messages.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center space-y-8 animate-in fade-in duration-700">
                    <img
                      src={isDarkTheme ? "/shared/logos/header/AskFinz_symbol_light.svg" : "/shared/logos/header/AskFinz_symbol_black.svg"}
                      alt="" className="opacity-20 h-10 object-contain mx-auto"
                    />
                    <div className="space-y-2">
                      <p className="text-accent text-sm font-black uppercase tracking-[0.2em]">askFinz Intelligence</p>
                      <p className="text-[10px] text-text-secondary max-w-[200px] leading-relaxed opacity-60">
                        Ask anything · Type <kbd className="px-1 py-0.5 rounded bg-secondary border border-border-color/30 font-mono">/</kbd> for commands · <kbd className="px-1 py-0.5 rounded bg-secondary border border-border-color/30 font-mono">@</kbd> to tag files
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 w-full max-w-[280px]">
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => { setInput(s); textareaRef.current?.focus() }}
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-border-color bg-surface-container hover:bg-state-hover transition-all text-left group"
                        >
                          <Search size={11} className="text-text-secondary group-hover:text-text-primary transition-colors shrink-0" />
                          <span className="text-[11px] text-text-secondary group-hover:text-text-primary transition-colors">{s}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="py-2">
                    {messages.map((m, msgIndex) => {
                      const isUser = m.role === "user"
                      const { thought, response } = !isUser ? parseMessageContent(m.content) : { thought: null, response: m.content }
                      const modelEntry = m.model ? ALL_MODELS.find((x) => x.id === m.model) : null
                      const modelDisplayLabel = m.model?.startsWith("auto:")
                        ? `Auto → ${m.model.slice(5)}`
                        : (modelEntry?.label ?? appName)

                      if (isUser) {
                        return (
                          <div key={m.id} className="flex flex-col items-end gap-1 px-1 pt-3 pb-1 group/usermsg">
                            {/* Attachments */}
                            {m.attachments && m.attachments.length > 0 && (
                              <div className="flex gap-1.5 flex-wrap justify-end max-w-[85%] mb-1">
                                {m.attachments.map((att, idx) => (
                                  <div key={idx}>
                                    {att.type === "image"
                                      ? <img src={att.dataUrl} alt={att.name} className="h-14 w-14 object-cover rounded-xl border border-border-color" />
                                      : (
                                        <div className="h-10 px-3 flex items-center gap-2 rounded-xl border border-border-color bg-secondary">
                                          <FileText size={13} className="text-text-secondary shrink-0" />
                                          <span className="text-[10px] text-text-secondary truncate max-w-[90px]">{att.name}</span>
                                        </div>
                                      )
                                    }
                                  </div>
                                ))}
                              </div>
                            )}

                            {editingId === m.id ? (
                              /* ── Edit mode ── */
                              <div className="max-w-[90%] w-full">
                                <textarea
                                  value={editingContent}
                                  onChange={(e) => setEditingContent(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") setEditingId(null)
                                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleEditSubmit(m.id, editingContent)
                                  }}
                                  autoFocus
                                  rows={Math.max(2, editingContent.split("\n").length)}
                                  className="w-full bg-bg-panel border border-accent/50 rounded-2xl px-4 py-3 text-sm text-text-primary resize-none outline-none leading-relaxed focus:border-accent transition-colors"
                                />
                                <div className="flex gap-2 mt-2 justify-end">
                                  <button
                                    onClick={() => setEditingId(null)}
                                    className="px-4 py-1.5 text-[12px] text-text-secondary hover:text-text-primary border border-border-color rounded-full transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleEditSubmit(m.id, editingContent)}
                                    disabled={isLoading || !editingContent.trim()}
                                    className="px-4 py-1.5 text-[12px] bg-accent text-bg-main rounded-full font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
                                  >
                                    Update
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* User bubble — theme-aware blue, right-aligned */}
                                <div className="max-w-[85%] px-5 py-3.5 rounded-[22px] text-[13px] leading-relaxed shadow-sm select-text"
                                  style={{
                                    background: "hsl(var(--af-user-bubble, 0 0% 88%))",
                                    color: "hsl(var(--af-user-bubble-fg, 0 0% 13%))",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}>
                                  {response}
                                </div>
                                {/* Hover actions */}
                                <div className="flex gap-0.5 opacity-0 group-hover/usermsg:opacity-100 transition-opacity mt-0.5">
                                  <button
                                    onClick={() => navigator.clipboard.writeText(m.content)}
                                    className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors"
                                    title="Copy"
                                  >
                                    <Copy size={12} />
                                  </button>
                                  <button
                                    onClick={() => { setEditingId(m.id); setEditingContent(m.content) }}
                                    className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors"
                                    title="Edit"
                                  >
                                    <Edit3 size={12} />
                                  </button>
                                  {!isLoading && (
                                    <button
                                      onClick={() => setInput(`Rewrite this prompt to be clearer and more effective, then show me the improved version:\n\n"${m.content}"`)}
                                      className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors"
                                      title="Rewrite prompt"
                                    >
                                      <Sparkles size={12} />
                                    </button>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )
                      }

                      // ── Assistant message ──
                      return (
                        <div key={m.id} className="flex px-1 pt-3 pb-1 group/assistmsg">
                          {/* Content column */}
                          <div className="flex-1 min-w-0">
                            {/* Tool calls */}
                            {m.toolCalls && m.toolCalls.length > 0 && (
                              <div className="mb-3 space-y-1">
                                {m.toolCalls.map((tc) => <ToolCallBlock key={tc.id} tc={tc} onResolve={resolvePermission} />)}
                              </div>
                            )}

                            {/* Thinking block */}
                            {(m.thinkingContent || thought) && (
                              <details className="mb-4 group/details marker:text-transparent" open={m.isStreaming}>
                                <summary className="cursor-pointer select-none flex items-center gap-2 text-[11px] font-medium text-text-secondary hover:text-text-primary transition-colors mb-2">
                                  <span className="flex items-center justify-center w-5 h-5 rounded border border-border-color bg-secondary group-open/details:bg-secondary transition-colors">
                                    <ChevronRight size={12} className="group-open/details:rotate-90 transition-transform duration-200" />
                                  </span>
                                  <Brain size={11} className="opacity-60" />
                                  {m.isStreaming && m.thinkingContent ? THINKING_PHASES_DRAWER[thinkingPhaseIdx] : (m.thoughtTimeMs ? `Thought for ${(m.thoughtTimeMs / 1000).toFixed(1)}s` : "Thinking process")}
                                </summary>
                                <div className="pl-7 pr-4 py-2 text-[12px] text-text-secondary/80 border-l-2 border-accent/30 ml-2.5 mb-3 font-mono leading-relaxed" style={{ whiteSpace: "pre-wrap" }}>
                                  {m.thinkingContent ?? thought}
                                </div>
                              </details>
                            )}

                            {/* Thinking phase indicator (no content yet, still streaming) */}
                            {!response && m.isStreaming && (
                              <button className="af-thinking mb-2">
                                <span className="af-thinking-shimmer">{THINKING_PHASES_DRAWER[thinkingPhaseIdx]}</span>
                              </button>
                            )}

                            {/* Response header + conditional body */}
                            {(() => {
                              const sources = extractDrawerSources(response)
                              const activeTab = drawerMsgTabs[m.id] ?? "answer"
                              const isComplex = detectComplexResponse(response)
                              const outTok = m.outputTokens ?? Math.ceil(response.length / 4)
                              const inTok  = m.inputTokens  ?? 0
                              const cacTok = m.cachedTokens ?? 0
                              const hasActualTokens = !!m.outputTokens
                              const totalTok = inTok + outTok
                              const pricing = MODEL_PRICING[m.model ?? ""]
                              const costs   = calcTokenCost(m.model ?? "", outTok, inTok, cacTok)

                              const mdComponents = {
                                code: CodeBlock,
                                // SECURITY V-074: protocol allow-list on LLM-emitted hrefs.
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                a: ({ node, href, ...props }: any) => {
                                  const safeHref = href && /^(https?:|mailto:|\/|#)/i.test(href) ? href : "#"
                                  return <a {...props} href={safeHref} target="_blank" rel="noopener noreferrer" style={{ color: "hsl(var(--af-accent, 217 91% 55%))", wordBreak: "break-all" }} />
                                },
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                table: ({ node, ...props }: any) => (
                                  <div style={{ overflowX: "auto", margin: "10px 0", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                                    <table style={{ minWidth: "100%", fontSize: 12 }} {...props} />
                                  </div>
                                ),
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                thead: ({ node, ...props }: any) => <thead style={{ background: "hsl(var(--muted) / 0.5)" }} {...props} />,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                tbody: ({ node, ...props }: any) => <tbody {...props} />,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                tr: ({ node, ...props }: any) => <tr {...props} />,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                th: ({ node, ...props }: any) => <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "hsl(var(--muted-foreground))", borderBottom: "1px solid hsl(var(--border))" }} {...props} />,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                td: ({ node, ...props }: any) => <td style={{ padding: "6px 10px", borderBottom: "1px solid hsl(var(--border) / 0.4)", color: "hsl(var(--foreground))" }} {...props} />,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                blockquote: ({ node, ...props }: any) => <blockquote style={{ borderLeft: "2px solid hsl(var(--muted-foreground) / 0.3)", paddingLeft: 10, margin: "6px 0", color: "hsl(var(--muted-foreground))" }} {...props} />,
                              }

                              return (
                                <>
                                  {/* Response header: logo + model + token count */}
                                  {response && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                      <div style={{
                                        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                                        background: "hsl(var(--af-accent, 217 91% 55%) / 0.12)",
                                        border: "1px solid hsl(var(--af-accent, 217 91% 55%) / 0.2)",
                                        display: "grid", placeItems: "center",
                                      }}>
                                        <img
                                          src={isDarkTheme ? "/shared/logos/header/AskFinz_symbol_light.svg" : "/shared/logos/header/AskFinz_symbol_black.svg"}
                                          alt="askFinz"
                                          width={14}
                                          height={14}
                                        />
                                      </div>
                                      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2, color: "hsl(var(--foreground))", display: "flex", alignItems: "center", gap: 4 }}>
                                          {modelDisplayLabel}
                                          {m.isStreaming && <Loader2 size={9} className="animate-spin" style={{ opacity: 0.5 }} />}
                                        </span>
                                        {!m.isStreaming && (
                                          <div style={{ position: "relative", display: "inline-flex" }} className="group/tokenwrap">
                                            <span style={{ fontSize: 10, fontFamily: "var(--af-mono)", lineHeight: 1.2, color: "hsl(var(--muted-foreground))", cursor: "default" }}>
                                              {!hasActualTokens ? "~" : ""}{(totalTok > 0 ? totalTok : outTok).toLocaleString()} tokens
                                              {costs.isFree ? " · free" : costs.isFreeTier ? " · free tier" : costs.totalCost > 0 ? ` · ${fmtCost(costs.totalCost)}` : ""}
                                            </span>
                                            {/* Hover card */}
                                            <div className="opacity-0 group-hover/tokenwrap:opacity-100 pointer-events-none group-hover/tokenwrap:pointer-events-auto transition-opacity"
                                              style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, minWidth: 260, background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, padding: "10px 12px", zIndex: 100, boxShadow: "0 8px 24px hsl(0 0% 0% / 0.15)", whiteSpace: "nowrap" }}>
                                              <div style={{ fontSize: 10, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                                                <span>Token Usage</span>
                                                <span style={{ color: costs.isFree || costs.isFreeTier ? "hsl(142 71% 45%)" : "hsl(var(--foreground))" }}>
                                                  {costs.isFree ? "Free model" : costs.isFreeTier ? "Free tier" : fmtCost(costs.totalCost)}
                                                </span>
                                              </div>
                                              <div style={{ borderTop: "1px solid hsl(var(--border))", paddingTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
                                                {inTok > 0 && (
                                                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "hsl(var(--muted-foreground))" }}>
                                                    <span>Input</span>
                                                    <span style={{ fontFamily: "var(--af-mono)" }}>
                                                      {inTok.toLocaleString()} tok{pricing && !pricing.free && !pricing.freeTier ? ` · ${fmtRate(pricing.inputPerMTok)}` : ""}
                                                      {costs.inputCost > 0 && <span style={{ color: "hsl(var(--foreground))", marginLeft: 8 }}>{fmtCost(costs.inputCost)}</span>}
                                                    </span>
                                                  </div>
                                                )}
                                                {cacTok > 0 && (
                                                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "hsl(var(--muted-foreground))" }}>
                                                    <span>Cached</span>
                                                    <span style={{ fontFamily: "var(--af-mono)" }}>
                                                      {cacTok.toLocaleString()} tok{pricing?.cachedPerMTok ? ` · ${fmtRate(pricing.cachedPerMTok)}` : ""}
                                                      {costs.cachedCost > 0 && <span style={{ color: "hsl(var(--foreground))", marginLeft: 8 }}>{fmtCost(costs.cachedCost)}</span>}
                                                    </span>
                                                  </div>
                                                )}
                                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "hsl(var(--muted-foreground))" }}>
                                                  <span>Output</span>
                                                  <span style={{ fontFamily: "var(--af-mono)" }}>
                                                    {outTok.toLocaleString()} tok{pricing && !pricing.free && !pricing.freeTier ? ` · ${fmtRate(pricing.outputPerMTok)}` : ""}
                                                    {costs.outputCost > 0 && <span style={{ color: "hsl(var(--foreground))", marginLeft: 8 }}>{fmtCost(costs.outputCost)}</span>}
                                                  </span>
                                                </div>
                                              </div>
                                              <div style={{ borderTop: "1px solid hsl(var(--border))", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                                                <span style={{ color: "hsl(var(--muted-foreground))" }}>Total{!hasActualTokens ? " (estimated)" : ""}</span>
                                                <span style={{ fontFamily: "var(--af-mono)", fontWeight: 600, color: "hsl(var(--foreground))" }}>
                                                  {(totalTok > 0 ? totalTok : outTok).toLocaleString()} tokens
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Content: card for complex/sourced responses; plain prose for simple */}
                                  {(isComplex || sources.length > 0) ? (
                                    <div style={{ borderRadius: 10, border: "1px solid hsl(var(--border) / 0.5)", overflow: "hidden", marginTop: 2 }}>
                                      {/* Tab bar */}
                                      <div style={{ display: "flex", borderBottom: "1px solid hsl(var(--border) / 0.5)", background: "hsl(var(--muted) / 0.15)" }}>
                                        <button
                                          onClick={() => setDrawerMsgTabs(t => ({ ...t, [m.id]: "answer" }))}
                                          style={{
                                            padding: "6px 12px", fontSize: 11, fontWeight: 500, background: "transparent", border: 0, cursor: "pointer",
                                            borderBottom: activeTab === "answer" ? "2px solid hsl(var(--foreground))" : "2px solid transparent",
                                            marginBottom: -1,
                                            color: activeTab === "answer" ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                                          }}
                                        >Answer</button>
                                        {sources.length > 0 && (
                                          <button
                                            onClick={() => setDrawerMsgTabs(t => ({ ...t, [m.id]: "sources" }))}
                                            style={{
                                              padding: "6px 12px", fontSize: 11, fontWeight: 500, background: "transparent", border: 0, cursor: "pointer",
                                              borderBottom: activeTab === "sources" ? "2px solid hsl(var(--foreground))" : "2px solid transparent",
                                              marginBottom: -1,
                                              color: activeTab === "sources" ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                                            }}
                                          >Sources <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.6 }}>{sources.length}</span></button>
                                        )}
                                      </div>
                                      {/* Tab panel */}
                                      <div style={{ padding: 10 }}>
                                        {activeTab === "answer" && (
                                          <div className="prose prose-sm max-w-none text-[13px] leading-[1.75]
                                            prose-invert
                                            prose-p:my-2.5 prose-p:leading-[1.75]
                                            prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5
                                            prose-h1:text-base prose-h2:text-[14px] prose-h3:text-[13px]
                                            prose-h1:font-bold prose-h2:font-semibold prose-h3:font-semibold
                                            prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent prose-pre:border-0
                                            prose-strong:font-semibold
                                          " style={{ color: "hsl(var(--foreground))" }}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{response}</ReactMarkdown>
                                            {m.isStreaming && !m.toolCalls?.some((tc) => tc.running) && (
                                              <span className="inline-block w-1.5 h-3.5 bg-accent animate-pulse ml-0.5 align-text-bottom rounded-[1px]" />
                                            )}
                                          </div>
                                        )}
                                        {activeTab === "sources" && sources.length > 0 && (
                                          <div className="flex flex-col gap-1.5">
                                            {sources.map(s => (
                                              <a key={s.num} href={s.url ?? "#"} target={s.url ? "_blank" : undefined} rel="noopener noreferrer"
                                                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 8, background: "hsl(var(--muted) / 0.3)", border: "1px solid hsl(var(--border))", fontSize: 11, color: "hsl(var(--muted-foreground))", textDecoration: "none" }}
                                              >
                                                <span style={{ width: 20, height: 20, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", background: "hsl(var(--muted))", fontSize: 9, fontWeight: 700, color: "hsl(var(--foreground))", flexShrink: 0 }}>{s.domain[0]?.toUpperCase()}</span>
                                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.domain}</span>
                                                <span style={{ fontSize: 9, opacity: 0.4 }}>[{s.num}]</span>
                                              </a>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    /* Simple text — plain prose, no card */
                                    <div className="prose prose-sm max-w-none text-[13px] leading-[1.75]
                                      prose-invert
                                      prose-p:my-2 prose-p:leading-[1.7]
                                      prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
                                      prose-h1:text-base prose-h2:text-[14px] prose-h3:text-[13px]
                                      prose-h1:font-bold prose-h2:font-semibold prose-h3:font-semibold
                                      prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent prose-pre:border-0
                                      prose-strong:font-semibold
                                    " style={{ color: "hsl(var(--foreground))" }}>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{response}</ReactMarkdown>
                                      {m.isStreaming && !m.toolCalls?.some((tc) => tc.running) && (
                                        <span className="inline-block w-1.5 h-3.5 bg-accent animate-pulse ml-0.5 align-text-bottom rounded-[1px]" />
                                      )}
                                    </div>
                                  )}
                                </>
                              )
                            })()}

                            {/* Action bar */}
                            {!m.isStreaming && (
                              <div className="flex items-center gap-0.5 mt-2 opacity-0 group-hover/assistmsg:opacity-100 transition-opacity">
                                <button
                                  onClick={() => navigator.clipboard.writeText(m.content)}
                                  className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors"
                                  title="Copy response"
                                >
                                  <Copy size={12} />
                                </button>
                                {/* Insert response into the active editor (if a research writer is on the page). */}
                                {typeof window !== "undefined" && (window as any).__researchEditor && (
                                  <button
                                    onClick={() => {
                                      try {
                                        const ed = (window as any).__researchEditor
                                        const { response: txt } = parseMessageContent(m.content)
                                        const clean = (txt || m.content).trim()
                                        if (!clean) return
                                        // Use editor.insertContent so it lands at the current cursor.
                                        ed?.chain?.().focus().insertContent(clean).run?.()
                                        if (typeof window !== "undefined") {
                                          window.dispatchEvent(new CustomEvent("research:inserted-from-chat", { detail: { length: clean.length } }))
                                        }
                                      } catch {}
                                    }}
                                    className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors"
                                    title="Insert into document"
                                  >
                                    <FileText size={12} />
                                  </button>
                                )}
                                {msgIndex === messages.length - 1 && (
                                  <button
                                    onClick={() => {
                                      const lastUserMsg = messages.slice(0, msgIndex).filter((x) => x.role === "user").at(-1)
                                      if (lastUserMsg) handleEditSubmit(lastUserMsg.id, lastUserMsg.content)
                                    }}
                                    className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors"
                                    title="Regenerate"
                                  >
                                    <RefreshCw size={12} />
                                  </button>
                                )}
                                <button className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary hover:text-green-400 transition-colors" title="Good response">
                                  <ThumbsUp size={12} />
                                </button>
                                <button
                                  className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-red-400 transition-colors"
                                  title="Needs improvement"
                                  onClick={() => { setDrawerDislikeModal({ open: true, msgId: m.id }); setDrawerDislikeFeedback("") }}
                                >
                                  <ThumbsDown size={12} />
                                </button>
                                <button
                                  className="p-1.5 rounded-lg hover:bg-secondary text-text-secondary hover:text-text-primary transition-colors"
                                  title="Download as .txt"
                                  onClick={() => {
                                    const { response: txt } = parseMessageContent(m.content)
                                    const blob = new Blob([`askFinz AI\n${"=".repeat(30)}\n${new Date().toLocaleString()}\n${"=".repeat(30)}\n\n${txt}`], { type: "text/plain" })
                                    const url = URL.createObjectURL(blob)
                                    const a = document.createElement("a"); a.href = url; a.download = `askfinz-${Date.now()}.txt`; a.click(); URL.revokeObjectURL(url)
                                  }}
                                >
                                  <Download size={12} />
                                </button>
                                <span className="ml-1.5 text-[10px] text-text-secondary/40 select-none">
                                  {m.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}

                {/* Dislike feedback modal */}
                {drawerDislikeModal.open && (
                  <div style={{ position: "fixed", inset: 0, background: "hsl(0 0% 0% / 0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setDrawerDislikeModal({ open: false, msgId: "" })}>
                    <div style={{ padding: 20, width: 340, display: "flex", flexDirection: "column", gap: 12, background: "hsl(var(--popover, var(--card)))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} onClick={e => e.stopPropagation()}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "hsl(var(--foreground))" }}>What could be improved?</div>
                      <textarea value={drawerDislikeFeedback} onChange={e => setDrawerDislikeFeedback(e.target.value)}
                        placeholder="Tell us what was wrong or how to improve…"
                        style={{ width: "100%", minHeight: 72, padding: "8px 12px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }}/>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button onClick={() => setDrawerDislikeModal({ open: false, msgId: "" })} style={{ padding: "5px 14px", borderRadius: 20, border: "1px solid hsl(var(--border))", background: "transparent", cursor: "pointer", fontSize: 12, color: "hsl(var(--muted-foreground))" }}>Cancel</button>
                        <button onClick={() => { console.log("[feedback]", drawerDislikeModal.msgId, drawerDislikeFeedback); setDrawerDislikeModal({ open: false, msgId: "" }) }} style={{ padding: "5px 14px", borderRadius: 20, border: 0, background: "hsl(var(--foreground))", color: "hsl(var(--background))", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Submit</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Input Area ──────────────────────────────────────────── */}
              <div className="p-4 shrink-0" style={{ background: "hsl(var(--header-footer-background, var(--card)))" }}>
                {/* Attachment previews */}
                {attachments.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {attachments.map((att, idx) => (
                      <AttachmentPreview key={idx} att={att} onRemove={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))} />
                    ))}
                  </div>
                )}

                {/* Card library picker popup */}
                {drawerCardPickerOpen && (() => {
                  const allCards = [...BUILTIN_CARDS, ...((() => { try { return JSON.parse(localStorage.getItem("askfinz_custom_cards") || "[]") } catch { return [] } })())]
                  const filtered = allCards.filter(c => !drawerCardSearch ||
                    c.label.toLowerCase().includes(drawerCardSearch.toLowerCase()) ||
                    c.group.toLowerCase().includes(drawerCardSearch.toLowerCase()) ||
                    c.tags.some((t: string) => t.toLowerCase().includes(drawerCardSearch.toLowerCase())))
                  const groups = [...new Set(filtered.map((c: any) => c.group))]
                  return (
                    <div className="mb-2 bg-bg-panel border border-border-color rounded-xl shadow-2xl overflow-hidden" style={{ maxHeight: 260, display: "flex", flexDirection: "column" }}>
                      <div className="px-3 py-2 border-b border-border-color flex-shrink-0">
                        <input autoFocus value={drawerCardSearch} onChange={e => setDrawerCardSearch(e.target.value)}
                          placeholder="Search cards…"
                          className="w-full bg-transparent text-[12px] text-text-primary outline-none placeholder:text-text-secondary" />
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {filtered.length === 0
                          ? <div className="px-3 py-3 text-[11px] text-text-secondary text-center">No cards found.</div>
                          : groups.map((grp: any) => (
                            <div key={grp}>
                              <div className="px-3 py-1 text-[9.5px] font-bold uppercase tracking-wider text-text-secondary/60 bg-secondary/30">{grp}</div>
                              {filtered.filter((c: any) => c.group === grp).map((c: any) => (
                                <button key={c.id} type="button"
                                  onClick={() => {
                                    setInput(prev => `${prev}@${c.label} `)
                                    setDrawerCardPickerOpen(false); setDrawerCardSearch("")
                                    textareaRef.current?.focus()
                                  }}
                                  className="w-full flex items-baseline gap-2.5 px-3 py-1.5 text-left hover:bg-state-hover transition-colors"
                                >
                                  <span className="text-[11px] font-mono font-semibold text-accent shrink-0 whitespace-nowrap">@{c.label}</span>
                                  <span className="text-[10px] text-text-secondary truncate">{c.desc}</span>
                                </button>
                              ))}
                            </div>
                          ))}
                      </div>
                    </div>
                  )
                })()}

                {/* Slash command popup */}
                {slashPopup.open && filteredSlash.length > 0 && (
                  <div className="mb-2 bg-bg-panel border border-border-color rounded-xl shadow-2xl overflow-hidden">
                    {filteredSlash.map((sc, i) => (
                      <button
                        key={sc.cmd}
                        onClick={() => applySlashCommand(sc.cmd)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${i === slashPopup.idx ? "bg-accent/20 text-accent" : "hover:bg-state-hover text-text-primary"}`}
                      >
                        <span className="text-[11px] font-mono font-bold shrink-0 min-w-[100px]">/{sc.cmd}</span>
                        <span className="text-[11px] text-text-secondary truncate">{sc.description}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* @ mention popup — context providers (for discoverability) + files */}
                {mentionPopup.open && mentionItems.length > 0 && (
                  <div className="mb-2 bg-bg-panel border border-border-color rounded-xl shadow-2xl overflow-hidden">
                    {mentionItems.map((item, i) => {
                      const active = i === mentionPopup.idx
                      const cls = `w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${active ? "bg-accent/20 text-accent" : "hover:bg-state-hover text-text-primary"}`
                      if (item.kind === "provider") {
                        return (
                          <button key={`p:${item.name}`} onClick={() => applyMention(item)} className={cls}>
                            <Sparkles size={12} className="shrink-0 text-accent" />
                            <span className="text-[11px] font-mono font-semibold">@{item.name}{item.takesArg ? ":" : ""}</span>
                            <span className="text-[10px] text-text-secondary truncate">{item.description}</span>
                          </button>
                        )
                      }
                      return (
                        <button key={`f:${item.file.path}`} onClick={() => applyMention(item)} className={cls}>
                          {item.file.type === "folder" ? <FolderOpen size={12} className="shrink-0 text-blue-400" /> : <FileText size={12} className="shrink-0 text-text-secondary" />}
                          <span className="text-[11px] font-mono">{item.file.path}</span>
                        </button>
                      )
                    })}
                  </div>
                )}

                {incomingHandovers.map((h) => (
                  <div key={h.id} className="mb-2 flex flex-col gap-1.5 px-3 py-2 rounded-xl text-[12px] bg-accent/10 border border-accent/40 text-text-primary animate-in fade-in slide-in-from-bottom-1 duration-200">
                    <div className="flex items-center gap-1.5 font-semibold text-accent">
                      <Sparkles size={12} className="shrink-0" />
                      <span>Incoming handover from {h.from_surface}</span>
                    </div>
                    {h.message && <div className="text-text-secondary line-clamp-2">“{h.message}”</div>}
                    <div className="flex items-center gap-2 mt-0.5">
                      <button type="button" onClick={() => acceptHandover(h)} className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-accent text-bg-main hover:opacity-90 transition-opacity">Accept &amp; continue</button>
                      <button type="button" onClick={() => declineHandover(h.id)} className="px-2.5 py-1 rounded-full text-[11px] font-medium border border-border-color text-text-secondary hover:bg-state-hover transition-colors">Dismiss</button>
                    </div>
                  </div>
                ))}
                {(() => {
                  const hint = getSurfaceHint ? getSurfaceHint(input) : null
                  if (!hint) return null
                  const cls = "mb-2 flex items-center gap-1.5 px-2.5 py-1 self-start rounded-full text-[11px] font-medium bg-accent/10 border border-accent/30 text-accent animate-in fade-in duration-150"
                  return onSurfaceHintClick ? (
                    <button
                      type="button"
                      onClick={() => onSurfaceHintClick(input)}
                      title="Hand this task to the recommended surface"
                      className={cls + " hover:bg-accent/20 transition-colors cursor-pointer"}
                    >
                      <Sparkles size={11} className="shrink-0" />
                      <span>{hint}</span>
                      <ChevronRight size={11} className="shrink-0 opacity-70" />
                    </button>
                  ) : (
                    <div className={cls}>
                      <Sparkles size={11} className="shrink-0" />
                      <span>{hint}</span>
                    </div>
                  )
                })()}
                <form
                  onSubmit={(e) => { e.preventDefault(); sendMessage() }}
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); if (!isDragging) setIsDragging(true) }}
                  onDragLeave={(e) => { if (e.currentTarget === e.target) setIsDragging(false) }}
                  className={`relative flex flex-col bg-bg-panel border rounded-3xl p-3 shadow-2xl transition-all focus-within:border-accent/40 ${isDragging ? "border-accent border-dashed bg-accent/5" : "border-border-color"}`}
                >
                  {isDragging && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-3xl bg-bg-panel/80 text-xs text-accent pointer-events-none">
                      Drop image or file to attach
                    </div>
                  )}
                  <div className="relative min-h-[60px] flex flex-col">
                    <textarea
                      ref={textareaRef}
                      value={input}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      placeholder="Ask me anything…"
                      rows={1}
                      className="w-full bg-transparent text-sm text-text-primary placeholder:text-text-secondary outline-none resize-none pt-1 px-2 leading-[1.6] min-h-[40px] max-h-48 overflow-y-hidden askfinz-invisible-scrollbar"
                    />
                    {input && (
                      <button
                        type="button"
                        onClick={() => { setInput(""); setSlashPopup({ open: false, filter: "", idx: 0 }); setMentionPopup({ open: false, filter: "", idx: 0, start: -1 }) }}
                        className="absolute right-2 top-2 p-1 rounded-full hover:bg-secondary text-text-secondary transition-colors"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Action bar */}
                  <div className="flex items-center gap-0.5 mt-2 pt-2 border-t border-border-color">
                    {/* ── Left: Upload · @ · Cards · / · Memory · Snapshot ── */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.txt,.md,.csv,.json,.xml,.yaml,.yml"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-1.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-secondary transition-colors shrink-0"
                      title="Upload file or image"
                    >
                      <FileUp size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const newVal = input + "@"
                        setInput(newVal)
                        setMentionPopup({ open: true, filter: "", idx: 0, start: newVal.length - 1 })
                        textareaRef.current?.focus()
                      }}
                      className="p-1.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-secondary transition-colors shrink-0"
                      title="@ mention"
                    >
                      <AtSign size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => setDrawerCardPickerOpen(p => !p)}
                      className={`p-1.5 rounded-full transition-colors shrink-0 ${drawerCardPickerOpen ? "text-accent bg-accent/10" : "text-text-secondary hover:text-text-primary hover:bg-secondary"}`}
                      title="Card library"
                    >
                      <Shapes size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const newVal = input.endsWith("\n") || input === "" ? input + "/" : input + "\n/"
                        setInput(newVal)
                        setSlashPopup({ open: true, filter: "", idx: 0 })
                        textareaRef.current?.focus()
                      }}
                      className="p-1.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-secondary transition-colors shrink-0"
                      title="Slash commands"
                    >
                      <Slash size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => { if (typeof window !== "undefined") window.location.href = memoryHref }}
                      className="p-1.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-secondary transition-colors shrink-0"
                      title="Memory settings"
                    >
                      <CloudSync size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => setScreenshotMode((s) => !s)}
                      className={`p-1.5 rounded-full transition-colors shrink-0 ${screenshotMode ? "text-accent bg-accent/10" : "text-text-secondary hover:text-text-primary hover:bg-secondary"}`}
                      title="Snapshot-to-code"
                    >
                      <ScanQrCode size={14} />
                    </button>

                    {/* ── Center: Model selector icon toggle ── */}
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={() => setShowModelPanel(v => !v)}
                      title={selectedModel === "auto" && autoRouteLabel ? `Auto: ${autoRouteLabel}` : `Model: ${currentModelInfo?.label ?? selectedModel}`}
                      className={`p-1.5 rounded-full transition-colors shrink-0 ${
                        showModelPanel
                          ? "text-accent bg-accent/10"
                          : "text-text-secondary hover:text-text-primary hover:bg-secondary"
                      }`}
                    >
                      {selectedModel === "auto" ? <Sparkles size={14} /> : <Bot size={14} />}
                    </button>
                    <div className="flex-1" />

                    {/* ── Right: TTS · Call · Send/Stop ── */}
                    <button
                      type="button"
                      onClick={() => setTtsEnabled((t) => !t)}
                      className={`p-1.5 rounded-full transition-colors shrink-0 ${ttsEnabled ? "text-accent bg-accent/10" : "text-text-secondary hover:text-text-primary hover:bg-secondary opacity-50"}`}
                      title="Text-to-speech"
                    >
                      <AudioLines size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowCallDialog(true)}
                      className={`p-1.5 rounded-full transition-colors shrink-0 ${showCallDialog ? "text-accent bg-accent/10" : "text-text-secondary hover:text-text-primary hover:bg-secondary opacity-50"}`}
                      title="Call askFinz — continue by phone"
                    >
                      <Phone size={14} />
                    </button>

                    {isLoading ? (
                      <button
                        type="button"
                        onClick={stopStreaming}
                        className="flex h-7 w-7 items-center justify-center rounded-full transition-all active:scale-95 bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 shrink-0"
                        title="Stop generation"
                      >
                        <Square size={12} fill="currentColor" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={!input.trim() && attachments.length === 0}
                        className={`flex h-7 w-7 items-center justify-center rounded-full transition-all active:scale-95 shrink-0 ${
                          input.trim() || attachments.length > 0
                            ? "bg-accent text-bg-main hover:brightness-110"
                            : "bg-secondary text-text-secondary cursor-not-allowed"
                        }`}
                        title="Send message"
                      >
                        <ChevronUp size={16} strokeWidth={3} />
                      </button>
                    )}
                  </div>
                  {/* Hints + disclaimer — inside form */}
                  <div style={{ textAlign: "center", marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: "hsl(var(--muted-foreground) / 0.6)", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>
                      Enter to send · Shift+Enter new line · / commands · @ files
                    </span>
                    <span style={{ fontSize: 8.5, color: "hsl(var(--muted-foreground) / 0.45)", lineHeight: 1.4 }}>
                      AI responses may be inaccurate. Always verify critical information independently.
                    </span>
                  </div>
                </form>
              </div>
            </>
          )}

          {/* ── Swarm Tab ─────────────────────────────────────────────────── */}
          {activeTab === "swarm" && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Swarm controls */}
              <div className="shrink-0 p-2 border-b border-border-color bg-secondary/30">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] text-muted-foreground font-semibold">Cells:</span>
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      onClick={() => setSwarmCells(Array.from({ length: n }, (_, i) => swarmCells[i] ?? { id: generateId(), messages: [], isRunning: false, abortRef: { current: null } }))}
                      className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${swarmCells.length === n ? "border-accent text-accent bg-accent/10" : "border-ide-divider text-muted-foreground hover:border-accent/50"}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input
                    value={swarmInput}
                    onChange={(e) => setSwarmInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); broadcastToSwarm() } }}
                    placeholder="Broadcast to all agents…"
                    className="flex-1 text-xs bg-transparent border border-ide-divider rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-accent/40 focus:border-accent/40"
                  />
                  <button
                    onClick={broadcastToSwarm}
                    disabled={!swarmInput.trim()}
                    className="px-3 py-1.5 text-xs rounded-lg bg-accent/20 hover:bg-accent/30 text-accent transition-colors disabled:opacity-40"
                  >
                    <Send size={13} />
                  </button>
                </div>
              </div>

              {/* Grid of swarm cells */}
              <div
                className={`flex-1 overflow-hidden p-2 grid gap-2 ${
                  swarmCells.length <= 1 ? "grid-cols-1" :
                  swarmCells.length === 2 ? "grid-cols-1 grid-rows-2" :
                  "grid-cols-2 grid-rows-2"
                }`}
              >
                {swarmCells.map((cell, i) => (
                  effectiveProjectId ? (
                    <SwarmCellView
                      key={cell.id} cell={cell} index={i}
                      broadcast={swarmBroadcast} onBroadcast={setSwarmBroadcast}
                      projectId={effectiveProjectId} model={selectedModel} apiBaseUrl={apiBaseUrl}
                    />
                  ) : (
                    <div key={cell.id} className="flex items-center justify-center border border-ide-divider rounded-lg text-xs text-muted-foreground p-4 text-center">
                      Swarm mode requires a project context
                    </div>
                  )
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  )
}
